import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CheckCircle2, Compass, MapPinned, PlaneTakeoff, Search } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { PlanAvailabilityDialog } from "@/components/PlanAvailabilityDialog";
import { CountryLabel } from "@/components/CountryLabel";
import { CountrySafetySignal } from "@/components/CountrySafetySignal";
import { buildCountryReasoningMap, qualifyTradeoffAgainstFit } from "@/lib/reasoningQuality";
import { getCountryPreviewSlugByCode } from "@/lib/countryPreviewContent";
import { getCountryExploreContent } from "@/lib/countryExploreContent";
import { getCountryDailyPaceDescription, getPaceSignalState } from "@/lib/dailyPaceSignals";
import { getCountryRemoteCompatibilityLabel, getRemoteDecisionContrast, isRemoteRelevant } from "@/lib/remoteWorkSignals";
import { canUserAccessPlanCheckout } from "@/lib/planCheckoutAccess";
import { getGaPagePath, trackGaEvent } from "@/lib/ga4";
import { getReviewedLabel } from "@/lib/countryTrust";
import { getCountryCostRangePresentation } from "@/lib/currencyDisplay";
import { getMotivationLabel } from "@/lib/motivationOptions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getUserDisplayHeaders = () => {
  const headers = {};
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (browserTimeZone) {
    headers["X-User-Timezone"] = browserTimeZone;
  }
  if (navigator?.language) {
    headers["X-User-Locale"] = navigator.language;
  }
  return headers;
};

const LIFE_REFINER_LABELS = {
  arts_culture: "arts and culture",
  fine_dining: "food and dining quality",
  golf_access: "golf access",
  legalized_cannabis: "cannabis policy fit",
  nightlife_entertainment: "nightlife and entertainment",
  shopping: "shopping convenience",
  sightseeing: "sightseeing access",
  public_transportation: "strong public transportation",
  walkability: "walkable daily life",
  beach_environment: "coastal living",
  outdoor_activities: "outdoor access",
};

const hasMeaningfulText = (value) => typeof value === "string" ? value.trim().length > 0 : Boolean(value);

const sanitizeRenderableList = (items, limit = Infinity) => (
  Array.isArray(items)
    ? items
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean)
        .slice(0, limit)
    : []
);

const RESIDENCY_COMPARISON_PROFILES = {
  PT: {
    practicality: "One of the more straightforward self-funded EU options if your income proof is already solid.",
    routeShape: "Usually strongest through passive-income or remote-income pathways rather than local employment.",
    watchout: "The move feels easier when the income documentation and tax planning are handled early."
  },
  ES: {
    practicality: "Workable, but more conditional than Portugal once visa type and work status need to line up.",
    routeShape: "Usually non-lucrative or digital-nomad, depending on whether you will keep working remotely.",
    watchout: "The path gets messier if your actual income setup does not fit the visa you choose."
  },
  GR: {
    practicality: "Reasonably workable for a self-funded Mediterranean move, but not especially fast-moving.",
    routeShape: "Usually financial-independence or digital-nomad style rather than a straightforward employer-led route.",
    watchout: "Timing and consular paperwork can shape how smooth it feels in practice."
  },
  FR: {
    practicality: "Possible, but rarely the easiest route in this shortlist.",
    routeShape: "Usually visitor, family, or business-linked rather than a light-touch retire-abroad path.",
    watchout: "French administration can feel document-heavy, so preparation quality matters."
  },
  MX: {
    practicality: "One of the more workable non-EU options if you cleanly meet the financial bar.",
    routeShape: "Usually temporary residence based on income or savings, often used before locking in a longer-term base.",
    watchout: "Consular thresholds and interpretation can vary more than people expect."
  },
  CR: {
    practicality: "Relatively workable if your income profile clearly matches the visa category.",
    routeShape: "Usually pensionado or rentista, so it fits retirees and stable-income households better than employer-led moves.",
    watchout: "The pathway is only as solid as the proof behind your pension or non-salary income."
  },
  PA: {
    practicality: "Often more workable than it first looks for the right nationality or pension profile.",
    routeShape: "Usually pensionado or an economic-tie route rather than a generic retire-anywhere option.",
    watchout: "It becomes much less straightforward if you do not naturally fit a pension or qualifying tie."
  },
  TH: {
    practicality: "Can work well, but only if you clearly fit one of the formal long-stay buckets.",
    routeShape: "Usually retirement, LTR, or capital-based rather than a casual long-term remote stay.",
    watchout: "The lifestyle may look easy before the age, income, or capital thresholds get real."
  },
  MY: {
    practicality: "More selective than it looks, but strong for people who comfortably clear the financial bar.",
    routeShape: "Usually MM2H-style self-funded residence rather than a low-threshold nomad route.",
    watchout: "Program terms can shift, so this only stays attractive if your finances are safely above the line."
  },
  EC: {
    practicality: "Fairly workable for a lower-cost self-funded move.",
    routeShape: "Usually pension, investment, or steady-income based rather than sponsorship-driven.",
    watchout: "The formal route may be easier than the long-term systems follow-through."
  },
  CO: {
    practicality: "Possible, but more case-dependent than the easiest Latin American options.",
    routeShape: "Usually digital-nomad, pension, or investment linked rather than a simple retire-abroad default.",
    watchout: "The most workable option depends heavily on whether your income source fits a live visa category."
  },
  VN: {
    practicality: "Less straightforward for true long-term residency than its day-to-day value suggests.",
    routeShape: "Usually business, work-linked, or investor-style rather than an easy retire-or-remote default.",
    watchout: "Great lifestyle value does not automatically translate into a straightforward residency path."
  },
  DE: {
    practicality: "Strong if you have a clear work, freelance, or business case — weaker as a pure lifestyle move.",
    routeShape: "Usually employment, freelance, or business-linked rather than passive-income residency.",
    watchout: "It is less forgiving if you want the move to work without a clear economic purpose."
  },
  CA: {
    practicality: "High-value if you qualify, but one of the more selective pathways in this set.",
    routeShape: "Usually skilled migration, family, or employer-linked rather than a simple self-funded relocation.",
    watchout: "Points, eligibility, and slower timelines can make it feel heavy even when the destination is attractive."
  },
  NL: {
    practicality: "Practical for the right work or entrepreneur profile, but not an easy catch-all option.",
    routeShape: "Usually employer-sponsored, highly skilled, or business-linked rather than passive-income relocation.",
    watchout: "The route is much less workable if you need it to function without sponsorship or a strong business case."
  },
  NZ: {
    practicality: "Selective, but strong if you genuinely fit the country’s skills or investment logic.",
    routeShape: "Usually skilled migration, family, or investment route rather than a lifestyle-led move.",
    watchout: "Distance and tighter entry criteria raise the cost of getting the decision wrong."
  },
  AU: {
    practicality: "Attractive, but usually more selective than the lifestyle benefits suggest.",
    routeShape: "Usually skills, employer sponsorship, partner, or investment route rather than a light self-funded path.",
    watchout: "The process can become expensive and slow if you are not already a clear fit for a formal category."
  },
  JP: {
    practicality: "Can be strong for the right work or business profile, but not especially flexible.",
    routeShape: "Usually employment, business, or spouse route rather than retire-abroad or low-friction remote relocation.",
    watchout: "Language, administrative detail, and route specificity all raise the bar."
  },
  SG: {
    practicality: "Very strong if you have a real work or wealth pathway; weak as a generic relocation option.",
    routeShape: "Usually employment-pass, business, or high-net-worth route rather than passive-income residence.",
    watchout: "Excellent systems do not help much if you do not naturally fit a formal economic route."
  },
  IT: {
    practicality: "Workable for the right self-funded or remote-income profile, but not universally easy.",
    routeShape: "Usually elective residence or digital-nomad style rather than a straightforward local-job route.",
    watchout: "Administration can be slower and more uneven than the lifestyle upside suggests."
  },
  HR: {
    practicality: "Moderately workable, especially for remote-income households seeking an EU base.",
    routeShape: "Usually digital-nomad or self-funded stay rather than a major skills-migration route.",
    watchout: "Good lifestyle fit can hide the fact that the pathway is narrower than Portugal or Spain."
  },
  UY: {
    practicality: "Often more workable than bigger-name destinations if you want a calmer long-term base.",
    routeShape: "Usually self-funded or income-based residence rather than employer-led migration.",
    watchout: "It is practical, but the value equation is weaker if you are expecting low-cost Latin America."
  },
  AR: {
    practicality: "Can be workable on paper, but the operating environment asks for more tolerance.",
    routeShape: "Usually pension, rentista, or local-activity-linked rather than a polished remote-worker pathway.",
    watchout: "Economic volatility can matter almost as much as the formal route itself."
  },
  CL: {
    practicality: "Reasonably structured, but not especially low-friction.",
    routeShape: "Usually work-, income-, or family-linked rather than an easy retire-abroad route.",
    watchout: "It is more practical if you value stability enough to accept a more formal process."
  }
};

const PLAN_REGION_GUIDANCE = {
  PT: {
    places: ["Lisbon", "Porto", "Algarve"],
    rationale: "Start with Lisbon for access, Porto for day-to-day value, and the Algarve only after slower coastal living still looks practical on healthcare and housing.",
    healthcareNote: "Healthcare depth is strongest in Lisbon and Porto; the Algarve works better once private-care setup is part of the plan.",
    budgetNote: "Lisbon resets the budget fastest. Porto and parts of the Algarve usually keep the monthly picture more manageable."
  },
  ES: {
    places: ["Valencia", "Málaga", "Madrid"],
    rationale: "Start with Valencia or Málaga if you want Mediterranean living without defaulting to the highest-cost Spanish option first; use Madrid only if city-scale access matters more.",
    healthcareNote: "Major-hub access is strongest in Madrid and the larger coastal cities; provider depth narrows once you move well outside them.",
    budgetNote: "Madrid and Barcelona tighten the cost case fastest. Valencia and Málaga often keep the tradeoff more manageable."
  },
  GR: {
    places: ["Athens", "Thessaloniki", "Crete"],
    rationale: "Start with Athens for year-round access, Thessaloniki for a calmer urban base, and Crete only after healthcare and logistics still hold up.",
    healthcareNote: "Athens carries the deepest private-care network; outside the main hubs, specialist access narrows faster.",
    budgetNote: "Athens is the clearest testing ground. Islands and seasonal markets change the cost picture more than the national average suggests."
  },
  UY: {
    places: ["Montevideo", "Punta del Este", "Colonia"],
    rationale: "Start with Montevideo for full-system practicality, then test Punta del Este or Colonia only if pace and lifestyle still justify the narrower service base.",
    healthcareNote: "Montevideo gives you the deepest provider network; outside it, specialist access thins out faster.",
    budgetNote: "Montevideo usually sets the real monthly benchmark. Resort areas can weaken the value case quickly."
  },
  CR: {
    places: ["San José", "Escazú / Santa Ana", "Central Valley"],
    rationale: "Start in the San José and Central Valley corridor first because healthcare, schools, and day-to-day logistics stay more predictable there than they do on the coasts.",
    healthcareNote: "Private-care access is strongest in the Central Valley; coastal living usually adds distance and more planning for specialist care.",
    budgetNote: "The housing and transport equation usually stays more manageable in the Central Valley than in the beach corridors people compare first."
  },
  MX: {
    places: ["Mexico City", "Mérida", "Querétaro"],
    rationale: "Start with cities that can carry healthcare, airport access, and daily setup at the same time; then test lifestyle-led alternatives only if the systems case still holds.",
    healthcareNote: "Mexico City carries the deepest specialist base; outside the biggest metro corridors, private-provider choice matters more.",
    budgetNote: "Mexico City changes the budget faster than the rest of the shortlist. Mérida and Querétaro often keep the setup steadier."
  },
  PA: {
    places: ["Panama City", "Boquete", "Coronado"],
    rationale: "Start with Panama City if you need the fullest healthcare and admin setup, then test Boquete or Coronado only if the slower-living tradeoff still looks worth it.",
    healthcareNote: "Panama City carries the strongest private network; outside it, flexibility narrows faster.",
    budgetNote: "Panama City sets the real premium case. Smaller expat hubs work better when slower pace matters more than city convenience."
  },
  TH: {
    places: ["Bangkok", "Chiang Mai", "Phuket"],
    rationale: "Start with Bangkok for medical and admin depth, then compare Chiang Mai or Phuket only after the long-stay route and daily setup still look workable.",
    healthcareNote: "Bangkok is still the strongest testing ground for specialist care and hospital quality.",
    budgetNote: "Bangkok raises the monthly base, while Chiang Mai often keeps the cost case more manageable if day-to-day air quality and seasonality still work for you."
  },
  MY: {
    places: ["Kuala Lumpur", "Penang", "Johor Bahru"],
    rationale: "Start with Kuala Lumpur for full-system practicality, then compare Penang if healthcare and pace matter more than city scale.",
    healthcareNote: "Kuala Lumpur and Penang carry the strongest private-hospital depth.",
    budgetNote: "Kuala Lumpur gives the clearest benchmark; Penang can still work well if healthcare matters more than city-scale job access."
  },
  FR: {
    places: ["Lyon", "Bordeaux", "Toulouse"],
    rationale: "Start outside Paris first if you want to see whether the lifestyle and healthcare case still holds without the highest housing pressure.",
    healthcareNote: "Major-city systems stay strong, but local-language comfort and admin setup still matter in every region.",
    budgetNote: "Paris changes the budget too quickly to serve as the default benchmark for the plan."
  },
  DE: {
    places: ["Berlin", "Munich", "Hamburg"],
    rationale: "Start with the cities that match your work or systems needs first, then test whether the housing tradeoff still looks worth carrying.",
    healthcareNote: "Specialist depth is strongest in the major metro corridors, but insurance and referral flow still shape access.",
    budgetNote: "Munich tightens the budget case fastest; Berlin and Hamburg often show the clearer comparison first."
  },
  NL: {
    places: ["Amsterdam", "Utrecht", "Eindhoven"],
    rationale: "Start by testing the Dutch systems advantage against the housing reality in the cities you could actually secure.",
    healthcareNote: "Healthcare is reliable, but city choice still affects referral flow, access speed, and daily convenience.",
    budgetNote: "Amsterdam makes the housing pressure visible fastest; Utrecht and Eindhoven often show whether the broader Dutch case still holds."
  },
  IT: {
    places: ["Milan", "Bologna", "Puglia"],
    rationale: "Start with Milan or Bologna if systems and healthcare matter most, then test the southern lifestyle case only after the practical setup still works.",
    healthcareNote: "Healthcare is more reliable in the stronger northern corridors; regional variation matters more here than it does in Spain or Portugal.",
    budgetNote: "Northern hubs raise the monthly base, but they also show the strongest systems case. Southern regions can improve value if service tradeoffs still look acceptable."
  },
  HR: {
    places: ["Zagreb", "Split", "Istria"],
    rationale: "Start with Zagreb for daily setup, then compare Split or Istria only if the coastal lifestyle still works once housing and services are priced in.",
    healthcareNote: "Zagreb usually gives the strongest provider depth; coastal living is more location-dependent on healthcare access.",
    budgetNote: "Seasonal coastal pricing changes the equation faster than Zagreb does."
  },
  SG: {
    places: ["Central Core", "River Valley", "East Coast"],
    rationale: "Start with neighborhoods that keep commute, healthcare, and daily execution tight, because location discipline matters more here than broad city choice.",
    healthcareNote: "Healthcare quality is strongest across the main private corridors, but cost and coverage structure do more of the work here.",
    budgetNote: "Rent is the main filter here. The decision only holds if housing, schooling, and healthcare all stay aligned together."
  },
  JP: {
    places: ["Tokyo", "Yokohama", "Fukuoka"],
    rationale: "Start with cities that can carry language support, admin reliability, and healthcare access at the same time before treating lifestyle fit as enough on its own.",
    healthcareNote: "Tokyo and Yokohama give the strongest provider depth and international-clinic access.",
    budgetNote: "Tokyo shows the cost reality fastest. Fukuoka can look more workable if cost matters more than maximum city scale."
  },
  CA: {
    places: ["Calgary", "Ottawa", "Halifax"],
    rationale: "Start where housing, healthcare, and daily execution still move together instead of testing the most expensive markets first.",
    healthcareNote: "Major-city access is steady, but provincial rules and specialist wait times still shape the practical experience.",
    budgetNote: "Toronto and Vancouver tighten the budget case fastest, so they are rarely the best first benchmark for this plan."
  },
  AU: {
    places: ["Brisbane", "Adelaide", "Melbourne"],
    rationale: "Start with cities that keep healthcare, housing, and daily livability in balance before testing the most expensive prestige markets first.",
    healthcareNote: "Healthcare is strongest in the larger metros, but private cover changes flexibility more than many newcomers expect.",
    budgetNote: "Sydney and Melbourne tighten the housing equation fastest. Brisbane or Adelaide often show the more practical option."
  },
  NZ: {
    places: ["Auckland", "Wellington", "Christchurch"],
    rationale: "Start with the cities that can carry healthcare, infrastructure, and daily setup before letting scenery drive the shortlist.",
    healthcareNote: "Auckland and Wellington usually carry the deepest provider networks; regional options narrow sooner than people expect.",
    budgetNote: "Housing costs in Auckland need a closer look early if the plan depends on staying flexible."
  },
  EC: {
    places: ["Quito", "Cuenca", "Manta"],
    rationale: "Start with cities that can still carry healthcare and routine setup without losing the lower-cost value case.",
    healthcareNote: "Quito and Cuenca usually offer the stronger private-care options.",
    budgetNote: "The lower-cost case stays more manageable away from the most imported-service-heavy neighborhoods."
  },
  CO: {
    places: ["Bogotá", "Medellín", "Pereira"],
    rationale: "Start with the stronger urban corridors first because provider quality, admin ease, and neighborhood-level safety vary more here than the country average suggests.",
    healthcareNote: "Bogotá and Medellín carry the deepest specialist networks.",
    budgetNote: "Medellín often reads stronger on value, but neighborhood-level pricing still changes the picture quickly."
  },
  AR: {
    places: ["Buenos Aires", "Mendoza", "Córdoba"],
    rationale: "Start with the cities that can best absorb volatility while still giving you a workable daily setup.",
    healthcareNote: "Buenos Aires still carries the deepest provider choice and private-care depth.",
    budgetNote: "The budget case can shift quickly with currency moves, so city-level housing and private-care pricing matter more here than usual."
  },
  CL: {
    places: ["Santiago", "Valparaíso", "Viña del Mar"],
    rationale: "Start with Santiago for the strongest systems case, then compare coastal living only if the tradeoff still works on healthcare and cost.",
    healthcareNote: "Santiago carries the strongest provider depth and private-care flexibility.",
    budgetNote: "Santiago sets the premium benchmark; the coast only stays attractive if slower pace matters more than city-scale access."
  },
  VN: {
    places: ["Ho Chi Minh City", "Da Nang", "Hanoi"],
    rationale: "Start with the cities that can carry international-hospital access and daily setup before treating value alone as the deciding case.",
    healthcareNote: "International-hospital depth is strongest in Ho Chi Minh City and Hanoi.",
    budgetNote: "Da Nang often keeps the cost case more manageable, but healthcare and service depth still narrow outside the main metros."
  }
};

const RELOCATION_BUDGET_DETAILS = {
  under_2000: { label: "Under $2,000/month", minAmount: 0, maxAmount: 1999 },
  "2000_2999": { label: "$2,000–$2,999/month", minAmount: 2000, maxAmount: 2999 },
  "3000_3999": { label: "$3,000–$3,999/month", minAmount: 3000, maxAmount: 3999 },
  "4000_5499": { label: "$4,000–$5,499/month", minAmount: 4000, maxAmount: 5499 },
  "5500_7499": { label: "$5,500–$7,499/month", minAmount: 5500, maxAmount: 7499 },
  "7500_plus": { label: "$7,500+/month", minAmount: 7500, maxAmount: 9500 },
  low: { label: "Under $2,000/month", minAmount: 0, maxAmount: 1999 },
  medium: { label: "$2,000–$3,999/month", minAmount: 2000, maxAmount: 3999 },
  high: { label: "$4,000–$5,499/month", minAmount: 4000, maxAmount: 5499 },
  very_high: { label: "$5,500+/month", minAmount: 5500, maxAmount: 9500 },
};

const BUDGET_FIT_TONE_STYLES = {
  Comfortable: "border-emerald-200 bg-emerald-50 text-emerald-800",
  Workable: "border-amber-200 bg-amber-50 text-amber-800",
  "Workable (leaning tight)": "border-yellow-200 bg-yellow-50 text-yellow-800",
  Tight: "border-orange-200 bg-orange-50 text-orange-800",
  Stretch: "border-rose-200 bg-rose-50 text-rose-800",
};

const BUDGET_FIT_PRIORITY = {
  Comfortable: 0,
  Workable: 1,
  "Workable (leaning tight)": 2,
  Tight: 3,
  Stretch: 4,
};

const stableHash = (value = "") => Array.from(String(value)).reduce(
  (acc, char) => ((acc << 5) - acc + char.charCodeAt(0)) >>> 0,
  7
);

const pickStableOption = (options, seed = "") => {
  if (!options.length) return "";
  return options[stableHash(seed) % options.length];
};

const RESIDENCY_PATHWAY_LABELS = {
  PT: { primary: "Passive-income / D7-style route", alternative: "Remote-income route" },
  ES: { primary: "Non-lucrative route", alternative: "Digital nomad route" },
  GR: { primary: "Financial-independence route", alternative: "Digital nomad route" },
  FR: { primary: "Visitor / self-funded route", alternative: "Business or family-linked route" },
  MX: { primary: "Temporary residence by income or savings", alternative: "Longer-term transition route" },
  CR: { primary: "Pensionado route", alternative: "Rentista route" },
  PA: { primary: "Pensionado route", alternative: "Economic-tie route" },
  TH: { primary: "Retirement route", alternative: "LTR or capital-based route" },
  MY: { primary: "MM2H-style route", alternative: "Higher-capital self-funded route" },
  EC: { primary: "Pension route", alternative: "Investment or steady-income route" },
  CO: { primary: "Digital nomad route", alternative: "Pension or investment route" },
  VN: { primary: "Business or work-linked route", alternative: "Investor-linked route" },
  DE: { primary: "Employment or freelance route", alternative: "Business route" },
  CA: { primary: "Skilled migration route", alternative: "Family or employer-linked route" },
  NL: { primary: "Highly skilled or employer-sponsored route", alternative: "Entrepreneur route" },
  NZ: { primary: "Skilled migration route", alternative: "Investment route" },
  AU: { primary: "Skills or employer-sponsored route", alternative: "Partner or investment route" },
  JP: { primary: "Employment route", alternative: "Business or spouse route" },
  SG: { primary: "Employment-pass route", alternative: "Business or wealth route" },
  IT: { primary: "Elective residence route", alternative: "Digital nomad route" },
  HR: { primary: "Digital nomad route", alternative: "Self-funded stay" },
  UY: { primary: "Self-funded residence route", alternative: "Income-based residence route" },
  AR: { primary: "Rentista route", alternative: "Pension or local-activity route" },
  CL: { primary: "Work or income-linked route", alternative: "Family-linked route" }
};

const PLAN_IDEA_GROUP_PATTERNS = {
  affordability: /budget|cost|afford|financial|value|buffer|price|monthly|expense|amenities/i,
  healthcare: /healthcare|medical|hospital|insurance|care access|wait/i,
  residency: /residency|visa|paperwork|pathway|route|consular|process|processing|permit|documentation|administration/i,
  language: /language|english|translation|local-language/i,
  infrastructure: /internet|infrastructure|transport|connectivity|utilities|logistics/i,
  lifestyle: /lifestyle|climate|pace|day-to-day|weather|coastal|urban|culture|quality-of-life|resident/i,
  work: /work|remote|employment|job|income|employer|self-employed|business/i,
  stability: /stability|predictability|volatility|policy|safety|risk/i,
  location: /region|regional|city|location|housing|hub|neighborhood/i,
};

const PLAN_IDEA_STOP_WORDS = new Set([
  "a", "an", "and", "are", "as", "at", "be", "by", "can", "country", "day", "for", "from", "if", "in", "into",
  "is", "it", "its", "may", "more", "of", "on", "or", "so", "still", "than", "that", "the", "their", "this",
  "to", "up", "with", "you", "your"
]);

const getSafetyLevel = (country) => Number(country?.safety_profile?.advisory_level || country?.travel_advisory_level || 0);
const getHealthcareConfidenceRank = (country) => {
  const label = String(country?.healthcare_confidence_label || "").toLowerCase();
  if (label.includes("strong")) return 3;
  if (label.includes("moderate")) return 2;
  if (label.includes("caution") || label.includes("lower")) return 1;
  return 2;
};
const getVisaDifficultyRank = (country) => ({ easy: 1, moderate: 2, hard: 3 }[String(country?.visa_difficulty || "").toLowerCase()] || 2);
const getHealthcareDisplayLabel = (country) => {
  const label = String(country?.healthcare_confidence_label || "").toLowerCase();
  if (label.includes("strong")) return "More consistent access";
  if (label.includes("moderate")) return "Mixed by location";
  if (label.includes("caution") || label.includes("lower")) return "More location-dependent";
  return "Mixed by location";
};

const HEALTHCARE_CARD_ALIGNMENT_OVERRIDES = {
  ES: {
    label: "More consistent access",
    summary: "Healthcare access is more consistent overall, but regional differences still matter once you choose a city and hospital network.",
    constraint: "Wait times and provider depth still vary enough by region that city choice matters."
  },
  PT: {
    label: "Reliable once established",
    summary: "Healthcare access is reliable once established, especially if you expect to use private clinics for faster appointments.",
    constraint: "Private insurance is often the smoother path if faster access or more English-speaking providers matters."
  },
  GR: {
    label: "More variable access",
    summary: "Specialist access is strongest around Athens and the larger private hubs, so island or smaller-town living usually needs more planning.",
    constraint: "Outside the biggest year-round hubs, provider choice and appointment flexibility narrow faster."
  },
  UY: {
    label: "Reliable once established",
    summary: "Healthcare access can feel reliable once established, especially through mutualista or private coverage, but options narrow outside Montevideo.",
    constraint: "Montevideo gives you the deepest network; outside it, provider choice and specialist access thin out faster."
  },
  FR: {
    label: "Strong but setup-heavy",
    summary: "Healthcare access is strong once established, though registration and paperwork take more setup than in lighter-entry systems.",
    constraint: "Initial registration steps take longer before care feels smooth day to day."
  },
  DE: {
    label: "Limited flexibility",
    summary: "Healthcare access is reliable, but insurance rules and provider pathways make the system less flexible day to day.",
    constraint: "Coverage choices still affect wait times and provider flexibility more than in lighter-touch systems."
  },
  CA: {
    label: "Reliable once established",
    summary: "Healthcare access is reliable for routine care, but non-urgent and specialist treatment usually moves slower than in the U.S.",
    constraint: "Provincial rules and specialist wait times matter more than many Americans expect."
  },
  NL: {
    label: "Limited flexibility",
    summary: "Care is reliable, but the gatekeeper-first model and your housing location shape how quickly specialist access feels workable day to day.",
    constraint: "Referral flow and city-level housing practicality matter more here than many newcomers expect."
  },
  NZ: {
    label: "Limited flexibility",
    summary: "Healthcare access is steady for routine care, but specialist treatment is less flexible on timing, especially outside the biggest centers.",
    constraint: "Regional access and wait times matter more once you move beyond the main population centers."
  },
  AU: {
    label: "Reliable once established",
    summary: "Healthcare access is reliable once established, though private cover matters more if you want elective care to move quickly.",
    constraint: "Private coverage matters more if faster elective access is important."
  },
  JP: {
    label: "Strong but setup-heavy",
    summary: "Healthcare access is strong, though language and appointment setup make it harder to use smoothly day to day.",
    constraint: "Routine appointments and hospital admin often need more local-language support."
  },
  SG: {
    label: "Reliable once established",
    summary: "Healthcare is fast and efficient once coverage is in place, but insurance structure and cost do more of the work than in the broader lifestyle-led options.",
    constraint: "The system feels most straightforward when pass-linked or private coverage details are already clear."
  },
  IT: {
    label: "More variable access",
    summary: "Healthcare access is more variable because regional differences can materially change the experience from one area to another.",
    constraint: "Provider quality and wait times vary more once you narrow to a city and local network."
  },
  HR: {
    label: "Reliable once established",
    summary: "Healthcare access is reliable once established, though faster specialist care often depends on private providers.",
    constraint: "Private care matters more if speed or specialist access is important."
  },
  CR: {
    label: "More variable access",
    summary: "Private healthcare is strongest around San José and the Central Valley, so coastal living can add travel time and more planning for specialist care.",
    constraint: "Outside the main corridor, hospital choice and routine specialist access thin out faster."
  },
  MX: {
    label: "Strong but setup-heavy",
    summary: "Healthcare access is strong if you are comfortable relying on private providers, but consistency depends more on what you are willing to pay.",
    constraint: "Private care matters more outside the strongest expat and metro zones."
  },
  TH: {
    label: "Strong but setup-heavy",
    summary: "Healthcare access is strong if you plan around private hospitals, but the public side is less likely to carry the same weight.",
    constraint: "Private insurance is often the practical route if predictable hospital access matters."
  },
  PA: {
    label: "Reliable once established",
    summary: "Healthcare access is reliable once established near Panama City, but it becomes less flexible farther out.",
    constraint: "Private insurance is often the practical route if daily healthcare reliability matters."
  },
  EC: {
    label: "Strong but setup-heavy",
    summary: "Healthcare access can work well through the main private corridors, but quality changes quickly once you move beyond them.",
    constraint: "Private care matters more outside Quito and the stronger expat corridors."
  },
  MY: {
    label: "Reliable once established",
    summary: "Healthcare access is reliable once you are using the private hospital network, which shapes the experience more than the public side.",
    constraint: "Private coverage is often the smoother path if predictable specialist access matters."
  },
  CO: {
    label: "More variable access",
    summary: "Healthcare access is more variable because provider quality shifts more from city to city than the headline suggests.",
    constraint: "Bogotá and Medellín offer the deepest networks; outside them, options narrow faster."
  },
  VN: {
    label: "Limited flexibility",
    summary: "Healthcare access has limited flexibility because international hospitals do most of the work and the experience drops off quickly outside that lane.",
    constraint: "Private international hospitals are often essential if you want steadier care."
  },
  AR: {
    label: "Limited flexibility",
    summary: "Healthcare access has limited flexibility because economic volatility makes day-to-day predictability the main tradeoff.",
    constraint: "Private care and location choice matter more if you want steady routine access."
  },
  CL: {
    label: "Limited flexibility",
    summary: "Healthcare access is reliable, but coverage choice affects speed, provider access, and daily flexibility earlier in the move.",
    constraint: "Public-versus-private coverage matters more if consistency is a main filter."
  },
};

const getAlignedHealthcareCardContent = (country) => {
  const override = HEALTHCARE_CARD_ALIGNMENT_OVERRIDES[country?.code];
  if (override) {
    return override;
  }

  const label = String(country?.healthcare_confidence_label || "").toLowerCase();
  const usRelative = String(country?.healthcare_us_relative || "").trim();
  const constraint = String(country?.healthcare_constraint_note || "").trim();

  if (label.includes("private")) {
    return {
      label: "Strong but setup-heavy",
      summary: usRelative
        ? `Healthcare access is strong once private coverage and provider setup are in place: ${usRelative}`
        : "Healthcare access is strong once private coverage and provider setup are in place.",
      constraint,
    };
  }

  if (label.includes("strong")) {
    return {
      label: "More consistent access",
      summary: usRelative
        ? `Healthcare access is more consistent overall: ${usRelative}`
        : "Healthcare access is more consistent overall.",
      constraint,
    };
  }

  if (label.includes("limited") || label.includes("lower")) {
    return {
      label: "Limited flexibility",
      summary: usRelative
        ? `Healthcare access has less flexibility here: ${usRelative}`
        : "Healthcare access has less flexibility here.",
      constraint,
    };
  }

  return {
    label: "More variable access",
    summary: usRelative
      ? `Healthcare access is more variable here: ${usRelative}`
      : "Healthcare access is more variable here.",
    constraint,
  };
};

const buildSafetyTradeoffLine = (country, summary = {}) => {
  const advisoryLevel = getSafetyLevel(country);
  const safetyFocused = (summary.priorities || []).includes("safety") || (summary.motivations || []).includes("safety");

  if (advisoryLevel >= 4) {
    return `${country.name} carries a severe U.S. advisory signal right now, so it should normally stay out of the active shortlist unless you have a highly specific reason to keep looking at it.`;
  }
  if (advisoryLevel === 3) {
    return `${country.name} needs much tighter city-level safety screening because the caution profile is meaningfully higher here than it is in steadier alternatives.`;
  }
  if (advisoryLevel === 2 && safetyFocused) {
    return `${country.name} can still work, but day-to-day stability will feel more location-dependent here than it does in the lower-concern cases.`;
  }

  return "";
};

const getPlanIdeaGroups = (text = "") => Object.entries(PLAN_IDEA_GROUP_PATTERNS)
  .filter(([, pattern]) => pattern.test(text))
  .map(([group]) => group);

const getPlanIdeaTokens = (text = "") => text
  .toLowerCase()
  .replace(/[’']/g, "")
  .replace(/[^a-z0-9\s]/g, " ")
  .split(/\s+/)
  .filter((token) => token && token.length > 2 && !PLAN_IDEA_STOP_WORDS.has(token))
  .map((token) => {
    if (token.endsWith("ing") && token.length > 5) return token.slice(0, -3);
    if (token.endsWith("ed") && token.length > 4) return token.slice(0, -2);
    if (token.endsWith("es") && token.length > 4) return token.slice(0, -2);
    if (token.endsWith("s") && token.length > 4) return token.slice(0, -1);
    return token;
  });

const arePlanIdeasTooSimilar = (left = "", right = "") => {
  const normalizedLeft = left.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
  const normalizedRight = right.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();

  if (!normalizedLeft || !normalizedRight) return false;
  if (normalizedLeft === normalizedRight) return true;
  if (normalizedLeft.includes(normalizedRight) || normalizedRight.includes(normalizedLeft)) return true;

  const leftGroups = getPlanIdeaGroups(left);
  const rightGroups = getPlanIdeaGroups(right);
  const sharedGroups = leftGroups.filter((group) => rightGroups.includes(group));
  const leftTokens = Array.from(new Set(getPlanIdeaTokens(left)));
  const rightTokens = Array.from(new Set(getPlanIdeaTokens(right)));
  const sharedTokens = leftTokens.filter((token) => rightTokens.includes(token));
  const smallestTokenCount = Math.max(1, Math.min(leftTokens.length, rightTokens.length));
  const tokenOverlap = sharedTokens.length / smallestTokenCount;

  return (sharedGroups.length >= 1 && tokenOverlap >= 0.34) || tokenOverlap >= 0.75;
};

const filterDistinctPlanIdeas = (ideas = [], blockedIdeas = [], limit = 2) => {
  const selected = [];

  for (const idea of ideas.filter(Boolean)) {
    if (selected.length >= limit) break;
    const overlapsBlocked = blockedIdeas.some((blockedIdea) => arePlanIdeasTooSimilar(idea, blockedIdea));
    const overlapsSelected = selected.some((selectedIdea) => arePlanIdeasTooSimilar(idea, selectedIdea));
    if (!overlapsBlocked && !overlapsSelected) {
      selected.push(idea);
    }
  }

  return selected;
};

const getCountryRegionGuidance = (country, summary = {}) => {
  const guidance = PLAN_REGION_GUIDANCE[country?.code];
  if (!guidance) return null;

  const healthcareMatters = ["very_important", "important"].includes(summary?.healthcare_importance);
  const budgetMatters = ["low", "medium"].includes(summary?.budget);
  const rationale = healthcareMatters && guidance.healthcareNote
    ? guidance.healthcareNote
    : budgetMatters && guidance.budgetNote
      ? guidance.budgetNote
      : guidance.rationale;

  return {
    places: sanitizeRenderableList(guidance.places, 4),
    rationale,
    healthcareNote: guidance.healthcareNote || "",
    budgetNote: guidance.budgetNote || "",
  };
};

const getRelocationBudgetDetails = (budgetValue) => {
  if (!budgetValue) return null;
  return RELOCATION_BUDGET_DETAILS[budgetValue] || null;
};

const getCountryTypicalBudgetBand = (countryCode, familyStatus) => {
  const slug = getCountryPreviewSlugByCode(countryCode);
  const content = slug ? getCountryExploreContent(slug) : null;
  const budgetBands = content?.costOverview?.budgetBands || [];
  if (!budgetBands.length) return null;

  const preferredIndex = ["couple", "family_young", "family_older"].includes(familyStatus) ? 1 : 0;
  return {
    ...(budgetBands[preferredIndex] || budgetBands[0]),
    bandIndex: budgetBands[preferredIndex] ? preferredIndex : 0,
  };
};

const parseDisplayedRange = (rangeText = "") => {
  const values = Array.from(rangeText.matchAll(/\d[\d,]*/g), (match) => Number(match[0].replace(/,/g, "")));
  if (!values.length) return null;
  if (values.length === 1) {
    return {
      min: values[0],
      max: /\+/.test(rangeText) ? Math.round(values[0] * 1.25) : values[0],
    };
  }
  return { min: values[0], max: values[1] };
};

const getBudgetFitAssessment = (budgetValue, rangeText = "") => {
  const relocationBudget = getRelocationBudgetDetails(budgetValue);
  const countryRange = parseDisplayedRange(rangeText);
  if (!relocationBudget || !countryRange) {
    return { fitLabel: "Workable", budgetPressureScore: 999 };
  }

  const userMin = relocationBudget.minAmount;
  const userMax = relocationBudget.maxAmount;
  const countryMin = countryRange.min;
  const countryMax = countryRange.max;
  const userSpan = Math.max(1, userMax - userMin);
  const countrySpan = Math.max(1, countryMax - countryMin);
  const aboveBudgetRatio = Math.max(0, countryMax - userMax) / countrySpan;
  const ceilingPosition = (countryMax - userMin) / userSpan;
  const midpointPosition = (((countryMin + countryMax) / 2) - userMin) / userSpan;

  let fitLabel = "Workable";
  if (countryMin >= userMax * 1.08 || aboveBudgetRatio >= 0.8) fitLabel = "Stretch";
  else if (aboveBudgetRatio >= 0.5 || (countryMin > ((userMin + userMax) / 2) && countryMax > userMax)) fitLabel = "Tight";
  else if (countryMax > userMax || ceilingPosition >= 0.85) fitLabel = "Workable (leaning tight)";
  else if (countryMax <= userMin * 0.88) fitLabel = "Comfortable";
  else if (countryMax <= userMax) fitLabel = midpointPosition <= 0.7 ? "Workable" : "Workable (leaning tight)";

  return { fitLabel, budgetPressureScore: ceilingPosition + (aboveBudgetRatio * 3) + (midpointPosition * 0.35) };
};

const stripRangeCadence = (text = "") => text.replace(/\s*\/\s*(month|year)\b/i, "").trim();

const buildBudgetFitRows = (countries = [], budgetValue, familyStatus) => {
  const rows = countries.slice(0, 4).map((country) => {
    const band = getCountryTypicalBudgetBand(country.code, familyStatus);
    const currencyPresentation = getCountryCostRangePresentation(country.code, band?.range || "");
    const range = stripRangeCadence(currencyPresentation.localRangeText || band?.range || "") || "Range varies";
    const usdRange = currencyPresentation?.localRangeText === currencyPresentation?.usdRangeText
      ? ""
      : `~${stripRangeCadence(currencyPresentation.usdRangeText || "")} USD`;
    const fitAssessment = getBudgetFitAssessment(budgetValue, band?.range || "");
    const regionBudgetNote = PLAN_REGION_GUIDANCE[country.code]?.budgetNote || "";
    return {
      code: country.code,
      name: country.name,
      range,
      usdRange,
      fitLabel: fitAssessment.fitLabel,
      detail: band?.detail || "",
      commentary: regionBudgetNote || getFirstSentence(band?.detail || "") || "City choice and housing pressure do more of the work than the national headline here.",
      budgetPressureScore: fitAssessment.budgetPressureScore,
    };
  });

  return rows.sort((a, b) => {
    const rankDelta = (BUDGET_FIT_PRIORITY[a.fitLabel] ?? 99) - (BUDGET_FIT_PRIORITY[b.fitLabel] ?? 99);
    if (rankDelta !== 0) return rankDelta;
    return a.budgetPressureScore - b.budgetPressureScore;
  });
};

const getFirstSentence = (text = "") => {
  if (!text) return "";
  const firstPart = text.split(/(?<=[.!?])\s+/)[0] || text;
  return firstPart.trim();
};

const buildBudgetSupportNote = (row, emphasis = "strength") => {
  if (!row) return "";
  const base = row.commentary.replace(/\.$/, "");
  const options = emphasis === "strength"
    ? [
        `${row.name} keeps the budget picture steadier when ${base.charAt(0).toLowerCase() + base.slice(1)}.`,
        `${row.name} still looks ${row.fitLabel.toLowerCase()} overall, but ${base.charAt(0).toLowerCase() + base.slice(1)}.`,
        `${row.name} remains one of the stronger financial fits once ${base.charAt(0).toLowerCase() + base.slice(1)}.`,
      ]
    : [
        `${row.name} is where budget pressure shows fastest once ${base.charAt(0).toLowerCase() + base.slice(1)}.`,
        `${row.name} carries the tighter margin here; ${base.charAt(0).toLowerCase() + base.slice(1)}.`,
        `${row.name} asks for the most location discipline because ${base.charAt(0).toLowerCase() + base.slice(1)}.`,
      ];
  return pickStableOption(options, `${row.code}-${emphasis}`);
};

const getBudgetRealityContext = (country, regionGuidance, summary = {}) => {
  const costIndex = Number(country?.cost_index || 0);
  if (!costIndex) return null;

  const budgetProfile = summary?.budget || "medium";
  const thresholds = budgetProfile === "low"
    ? { comfortable: 62, workable: 74 }
    : budgetProfile === "high"
      ? { comfortable: 84, workable: 92 }
      : { comfortable: 72, workable: 84 };

  if (costIndex <= thresholds.comfortable) {
    return {
      label: "Comfortable if location discipline holds",
      copy: regionGuidance?.budgetNote || "The monthly case usually stays more manageable here, but the most in-demand housing corridors still reset the budget faster than the national average suggests."
    };
  }
  if (costIndex <= thresholds.workable) {
    return {
      label: "Workable with city-level pricing",
      copy: regionGuidance?.budgetNote || "This stays workable, but housing and private-care convenience start to matter much more once you narrow to a real city."
    };
  }

  return {
    label: "Tighter once housing is real",
    copy: regionGuidance?.budgetNote || "Housing pressure changes the equation quickly here, so rent, insurance, and routine city costs need to be priced together early."
  };
};

const getResidencyPathwayDetail = (country, residencyComparison) => {
  const labels = RESIDENCY_PATHWAY_LABELS[country?.code] || {};
  return {
    primary: labels.primary || "Primary pathway to validate through the route shape below",
    alternative: labels.alternative || "",
    routeShape: residencyComparison?.routeShape || "",
    watchout: residencyComparison?.watchout || "",
  };
};

const getExpatFamiliaritySignal = (country, summary = {}) => {
  const shouldSurface = ["very_important", "important"].includes(summary?.expat_community_importance)
    || summary?.family_status === "retired"
    || ["long_term_relocation", "split_time_between_countries"].includes(summary?.intended_stay);

  if (!shouldSurface) return null;

  const tier = country?.us_retiree_presence?.tier || "Limited";
  const countrySpecificDescriptions = {
    PT: "Portugal has one of the most familiar soft-landings in this set, with visible expat hubs and a newcomer path many people can picture quickly.",
    ES: "Spain usually feels familiar faster because large expat zones, bigger-city infrastructure, and regional choice give you several workable ways in.",
    MX: "Mexico tends to feel familiar through city choice rather than one single expat corridor, so the experience depends heavily on where you settle.",
    CR: "Costa Rica often feels familiar fastest in its better-known expat corridors, especially where healthcare and day-to-day support are already easier to access.",
    PA: "Panama usually feels most familiar where the practical setup is already concentrated, especially around Panama City and the best-known expat zones.",
    UY: "Uruguay feels familiar in a quieter, steadier way, with most of that support concentrated around Montevideo and its immediate orbit.",
    IT: "Italy can feel familiar socially faster than it feels simple administratively, so the practical experience still depends a lot on region and city choice.",
    GR: "Greece often feels familiar where expat life, healthcare access, and daily convenience meet more cleanly, especially in Athens and the better-known hubs.",
    FR: "France can feel familiar once you are inside the right city or region, though the expat layer is less of the story than the broader system itself.",
    CA: "Canada often feels familiar through language, institutions, and everyday norms, even when city-level cost pressure is doing more of the practical work.",
    AU: "Australia usually feels easy to picture from day one because language, daily norms, and service expectations are already legible to many newcomers.",
    NZ: "New Zealand tends to feel familiar through pace, language, and social ease, though the practical network is narrower than in the larger-country options.",
    DE: "Germany can feel familiar once routines are established, but the practical side depends more on systems comfort than on a visibly expat-first landing.",
    NL: "The Netherlands often feels familiar quickly because daily systems, English usability, and newcomer infrastructure are relatively easy to navigate.",
    SG: "Singapore usually feels familiar through language ease and strong daily systems, even though the overall experience is more structured than it is casual.",
    HR: "Croatia can feel familiar in the right coastal or urban pockets, though that familiarity is more localized than broad across the country.",
    TH: "Thailand tends to feel familiar where expat infrastructure, private healthcare, and daily convenience already overlap clearly in the main hubs.",
    MY: "Malaysia often feels familiar quickly because English usability and expat-oriented daily infrastructure do a lot of the settling-in work.",
    JP: "Japan can feel orderly and dependable early on, even if true social familiarity still depends more on language comfort and neighborhood fit.",
    AR: "Argentina can feel socially engaging fast, but the practical side still depends more on your comfort with volatility than on expat familiarity alone.",
    CL: "Chile often feels familiar through structure and predictability more than through a deeply visible expat layer.",
    CO: "Colombia can feel familiar in the right city pockets, but the experience changes quickly with neighborhood, pace, and provider quality.",
    VN: "Vietnam tends to feel familiar where international neighborhoods and daily convenience already overlap, rather than across the country as a whole.",
    EC: "Ecuador can feel familiar in the main expat corridors, but that comfort narrows quickly once you move beyond the better-known bases.",
  };
  const baseDescription = countrySpecificDescriptions[country?.code] || country?.us_retiree_presence?.description || "Visible expat infrastructure is more limited here, so settling in depends more on location choice and personal setup.";
  const label = tier === "Established"
    ? "Established settling-in familiarity"
    : tier === "Moderate"
      ? "Moderate expat familiarity"
      : "Lighter expat infrastructure";

  const supportiveContextByCountry = {
    PT: "For Portugal, this matters most if you want the social side of the move to feel legible early rather than pieced together slowly over time.",
    ES: "For Spain, this matters when regional choice and newcomer familiarity are part of how you judge the move, not just the headline lifestyle case.",
    MX: "For Mexico, this matters because familiarity depends less on the country in general and more on whether your exact city and neighborhood still work in practice.",
    CR: "For Costa Rica, this matters most if a softer landing only counts when healthcare and daily support are already nearby.",
    PA: "For Panama, this matters when the ease of settling in depends on whether the country still works once the move narrows toward Panama City or its strongest hubs.",
    UY: "For Uruguay, this matters because the social ease is steadier than broad, and most of it still runs through Montevideo.",
    IT: "For Italy, this matters if the social appeal needs to hold up once the practical side of the move starts asking more of you.",
    GR: "For Greece, this matters when familiarity depends on being in the places where healthcare, convenience, and expat life overlap more cleanly.",
    FR: "For France, this matters when you want the country to feel livable quickly even though the system itself still asks for more adaptation.",
    CA: "For Canada, this matters because the move often feels culturally familiar before the cost and city-level tradeoffs are fully priced in.",
    AU: "For Australia, this matters if you value a move that feels understandable quickly rather than one that needs a longer adjustment curve.",
    NZ: "For New Zealand, this matters when the softer pace only works if the practical network still feels wide enough for your needs.",
    DE: "For Germany, this matters when familiarity depends more on comfort with systems than on an obviously expat-led landing.",
    NL: "For the Netherlands, this matters when English usability and newcomer infrastructure are part of what makes the move feel manageable early on.",
    SG: "For Singapore, this matters if predictability and English ease are important, even in a setting that feels more structured than relaxed.",
    HR: "For Croatia, this matters when you are relying on a few specific coastal or urban bases rather than broad country-wide familiarity.",
    TH: "For Thailand, this matters if expat familiarity only counts when healthcare, daily convenience, and visa fit still line up together.",
    MY: "For Malaysia, this matters when English ease and expat-friendly daily systems are part of what makes the move feel practical early on.",
    JP: "For Japan, this matters if order and predictability help, even though true day-to-day familiarity still depends much more on language comfort.",
    AR: "For Argentina, this matters when the social ease needs to hold up against a more volatile practical environment.",
    CL: "For Chile, this matters if familiarity is coming more from structure and predictability than from a large built-in expat layer.",
    CO: "For Colombia, this matters when the move only feels familiar in the right city pockets rather than across the country as a whole.",
    VN: "For Vietnam, this matters if the move depends on the handful of places where international convenience is already easier to access.",
    EC: "For Ecuador, this matters when the softer landing only holds up inside the better-known corridors rather than beyond them."
  };
  const supportiveContext = supportiveContextByCountry[country?.code]
    || (summary?.family_status === "retired"
      ? "This matters most once everyday support, English comfort, and long-stay social transition start carrying more weight than novelty alone."
      : ["very_important", "important"].includes(summary?.expat_community_importance)
        ? "This matters because community depth and English support can change how quickly the move starts to feel workable on the ground."
        : "This gives you a quick read on how much English support and long-stay familiarity usually show up once the shortlist moves into real planning.");

  return {
    label,
    copy: `${baseDescription} ${supportiveContext}`.trim(),
  };
};

const REGION_PLACE_TOOLTIP_OVERRIDES = {
  athens: "Strongest specialist healthcare depth and admin infrastructure in the country.",
  algarve: "Usually the easiest lifestyle landing for retirees and English-speaking support.",
  porto: "Often the better balance of cost, infrastructure, and livability.",
  lisbon: "Best overall access point for healthcare, air links, and day-to-day setup.",
  montevideo: "Strongest full-system practicality, healthcare depth, and expat familiarity in Uruguay.",
};

const getRegionPlaceContext = (place = "", index = 0) => {
  const slug = place.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  if (REGION_PLACE_TOOLTIP_OVERRIDES[slug]) {
    return REGION_PLACE_TOOLTIP_OVERRIDES[slug];
  }

  if (index === 0) return "Usually the strongest first test for healthcare depth, infrastructure, and admin ease.";
  if (index === 1) return "Often the more practical middle ground between cost pressure and daily practicality.";
  return "Worth comparing once pace, retiree familiarity, or lifestyle fit starts to matter more than maximum city access.";
};

const softenVerificationGuidance = (text = "", seed = "") => {
  const trimmed = (text || "").trim();
  if (!trimmed) return "";

  const replacements = [
    [
      /^Check\s+/i,
      [
        "The shortlist usually narrows faster once you look at ",
        "Most of the remaining uncertainty sits around ",
        "The strongest signal usually shows up when you narrow ",
      ],
    ],
    [
      /^Compare\s+/i,
      [
        "This comparison tends to settle once you compare ",
        "The stronger option usually stands out when you compare ",
        "The decision usually tightens once you compare ",
      ],
    ],
    [
      /^Validate\s+/i,
      [
        "This route becomes easier to evaluate after you validate ",
        "The remaining uncertainty usually sits around ",
        "The clearest signal usually appears after you validate ",
      ],
    ],
    [
      /^Confirm\s+/i,
      [
        "The choice usually steadies once you confirm ",
        "This part of the plan usually settles after you confirm ",
        "The decision usually tightens once you confirm ",
        "The shortlist usually firms up after you confirm ",
        "This route reads more clearly once you confirm ",
      ],
    ],
    [
      /^Resolve\s+/i,
      [
        "The shortlist usually settles once you resolve ",
        "Most of the remaining friction sits around ",
        "The final comparison usually steadies after you resolve ",
      ],
    ],
    [
      /^Pressure-test\s+/i,
      [
        "The strongest option is easier to trust once you look closely at ",
        "The choice usually sharpens once you look closely at ",
        "The comparison reads more clearly once you look closely at ",
      ],
    ],
  ];

  for (const [pattern, options] of replacements) {
    if (pattern.test(trimmed)) {
      const replacement = pickStableOption(options, `${seed}-${trimmed}`);
      return trimmed.replace(pattern, replacement).replace(/^The\s+the\s+/i, "The ");
    }
  }

  return trimmed;
};

const getPrimaryPlanIdeaGroup = (text = "") => getPlanIdeaGroups(text)[0] || "fallback";

const finalizeDistinctTradeoffs = (ideas = [], blockedIdeas = [], limit = 2) => {
  const selected = [];
  const usedPrimaryGroups = new Set();

  for (const idea of ideas.filter(Boolean)) {
    if (selected.length >= limit) break;

    const primaryGroup = getPrimaryPlanIdeaGroup(idea);
    const overlapsBlocked = blockedIdeas.some((blockedIdea) => blockedIdea && arePlanIdeasTooSimilar(idea, blockedIdea));
    const overlapsSelected = selected.some((selectedIdea) => arePlanIdeasTooSimilar(idea, selectedIdea));
    const repeatsPrimaryGroup = primaryGroup !== "fallback" && usedPrimaryGroups.has(primaryGroup);

    if (!overlapsBlocked && !overlapsSelected && !repeatsPrimaryGroup) {
      selected.push(idea);
      if (primaryGroup !== "fallback") {
        usedPrimaryGroups.add(primaryGroup);
      }
    }
  }

  return selected;
};

const RelocationPlan = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showPlanAvailabilityDialog, setShowPlanAvailabilityDialog] = useState(false);
  const hasTrackedBlueprintViewedRef = useRef(false);
  const reviewedLabel = getReviewedLabel();
  const showDebugPanel = useMemo(() => new URLSearchParams(location.search).get("debug") === "1", [location.search]);

  useEffect(() => {
    fetchPlan();
  }, []);

  useEffect(() => {
    if (!plan || hasTrackedBlueprintViewedRef.current) return;

    const shortlistedCountries = plan?.shortlisted_countries || [];
    trackGaEvent("blueprint_viewed", {
      page_path: getGaPagePath(),
      blueprint_tier: "paid",
      lead_country: shortlistedCountries[0]?.name || null,
      shortlisted_country_count: shortlistedCountries.length,
    });
    hasTrackedBlueprintViewedRef.current = true;
  }, [plan]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/plan`, {
        withCredentials: true,
        headers: getUserDisplayHeaders(),
      });
      setPlan(response.data);
    } catch (error) {
      console.error("Plan error:", error);
      if (error.response?.status === 403) {
        toast.error("Please upgrade to access your plan");
        navigate("/dashboard");
      } else if (error.response?.status === 400) {
        toast.error("Please complete the assessment first");
        navigate("/assessment");
      } else {
        toast.error("Failed to load plan");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    const canAccessCheckout = canUserAccessPlanCheckout(user);
    trackGaEvent("plan_unlock_clicked", {
      source: "relocation_plan",
      checkout_allowed: canAccessCheckout,
    });

    if (!canAccessCheckout) {
      setShowPlanAvailabilityDialog(true);
      return;
    }

    try {
      const response = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      trackGaEvent("checkout_started", {
        source: "relocation_plan",
        session_id: response.data.session_id,
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      if (error.response?.status === 403 && error.response?.data?.detail?.code === "plans_launch_pending") {
        setShowPlanAvailabilityDialog(true);
        return;
      }
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/plan/pdf`, {
        withCredentials: true,
        responseType: 'blob',
        headers: getUserDisplayHeaders(),
      });
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Relocation_Clarity_Plan.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      trackGaEvent("pdf_downloaded", {
        source: "relocation_plan",
        filename,
      });
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("PDF download error:", error);
      toast.error("Failed to download PDF. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const alignmentLevels = {
    strong: {
      icon: "🟢",
      label: "Strong Fit",
      className: "bg-emerald-50 text-emerald-700 border-emerald-200"
    },
    moderate: {
      icon: "🟡",
      label: "Moderate Fit",
      className: "bg-amber-50 text-amber-700 border-amber-200"
    },
    situational: {
      icon: "⚪",
      label: "Situational Fit",
      className: "bg-gray-50 text-gray-700 border-gray-200"
    }
  };

  const getPriorityAlignment = (score) => {
    if (score >= 85) return alignmentLevels.strong;
    if (score >= 70) return alignmentLevels.moderate;
    return alignmentLevels.situational;
  };

  const getHealthcareIndicator = (rating) => {
    if (rating >= 4.3) return alignmentLevels.strong;
    if (rating >= 3.8) return alignmentLevels.moderate;
    return alignmentLevels.situational;
  };

  const getResidencyIndicator = (difficulty) => {
    if (difficulty === "Easy") return alignmentLevels.strong;
    if (difficulty === "Moderate") return alignmentLevels.moderate;
    return alignmentLevels.situational;
  };

  const getEaseOfRelocationIndicator = (country) => {
    if (country.visa_difficulty === "Easy" && country.cost_index <= 70) return alignmentLevels.strong;
    if (country.visa_difficulty === "Moderate" || country.cost_index <= 85) return alignmentLevels.moderate;
    return alignmentLevels.situational;
  };

  const getFinancialIndicator = (costIndex) => {
    if (costIndex <= 70) return alignmentLevels.strong;
    if (costIndex <= 85) return alignmentLevels.moderate;
    return alignmentLevels.situational;
  };

  const renderIndicator = (indicator, testId) => (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${indicator.className}`}
    >
      <span>{indicator.icon}</span>
      {indicator.label}
    </span>
  );

  const countryRolePhrases = {
    PT: "European lifestyle, residency access, and long-term financial practicality",
    ES: "healthcare strength, stability, and quality-of-life depth",
    GR: "lifestyle and climate fit with a lighter-cost Mediterranean base",
    CR: "practical relocation dynamics and slower-lifestyle appeal",
    PA: "feasibility, financial practicality, and day-to-day setup ease",
    TH: "cost efficiency, lifestyle flexibility, and practical remote-living value",
    MY: "infrastructure, English usability, and value",
    JP: "systems strength, safety, and urban reliability",
    SG: "operational ease, infrastructure, and English-speaking practicality",
    NZ: "safety, outdoors, and English-speaking ease",
    AU: "urban lifestyle, healthcare confidence, and coastal livability",
    CA: "institutional stability and English-speaking familiarity",
    FR: "healthcare depth, system strength, and long-term practicality",
    DE: "infrastructure reliability and long-term execution strength",
    NL: "connected urban life and European accessibility",
    IT: "Mediterranean lifestyle and cultural depth",
    HR: "Adriatic lifestyle and easier-paced European living",
    UY: "calmer regional stability and lower-volatility living",
    MX: "value, flexibility, and accessible everyday living",
    EC: "lower-cost practicality and climate choice",
    CO: "urban energy and value",
    VN: "cost efficiency and dynamic city life",
    AR: "cultural depth and urban lifestyle variety",
    CL: "structured regional stability and outdoors access"
  };

  const getTradeoffs = (country) => {
    const tradeoffs = [];
    const safetyTradeoff = buildSafetyTradeoffLine(country, planSummary);
    if (safetyTradeoff) {
      tradeoffs.push(safetyTradeoff);
    }
    if (country?.visa_difficulty === "Hard") {
      tradeoffs.push("Residency paperwork requires more documentation, more sequencing, and longer processing time.");
    } else if (country?.visa_difficulty === "Moderate") {
      tradeoffs.push("Residency paperwork requires more documentation and longer processing time than the easiest routes.");
    }
    if (country?.language && !country.language.toLowerCase().includes("english")) {
      tradeoffs.push("Housing searches, healthcare appointments, and admin tasks require more local-language effort here.");
    }
    if (country?.cost_index >= 85) {
      tradeoffs.push("Monthly costs leave less room for unexpected housing or healthcare changes.");
    }
    if (country?.cost_index <= 65) {
      tradeoffs.push("Premium healthcare, housing quality, and expat-friendly services thin out faster outside the main hubs.");
    }
    if (country?.healthcare_rating && country.healthcare_rating < 4) {
      tradeoffs.push("Hospital quality, specialist access, and wait times vary more by city and region.");
    }
    if (!tradeoffs.length) {
      tradeoffs.push("Housing, paperwork, and daily logistics vary more by city and need verification.");
      tradeoffs.push("Rental supply, neighborhood quality, and service access change sharply from one city to another.");
    }
    return tradeoffs.slice(0, 3);
  };

  const getStrengths = (country) => {
    const highlights = country?.highlights?.length ? country.highlights : country?.highlights_detailed || [];
    return highlights.slice(0, 3);
  };

  const buildPlanCautionCandidates = (country, summary) => {
    const candidates = [];
    const residencyNote = getResidencyComparisonNote(country);
    const safetyTradeoff = buildSafetyTradeoffLine(country, summary);

    if (safetyTradeoff) {
      candidates.push(safetyTradeoff);
    }

    if (residencyNote?.watchout) {
      candidates.push(residencyNote.watchout);
    }

    if (country?.visa_difficulty === "Hard") {
      candidates.push("Residency paperwork requires more documentation, more sequencing, and longer processing time.");
    } else if (country?.visa_difficulty === "Moderate") {
      candidates.push("Residency paperwork requires more documentation and longer processing time than the easiest routes.");
    }

    if (country?.language && !country.language.toLowerCase().includes("english")) {
      candidates.push("Housing searches, healthcare appointments, and admin tasks require more local-language effort here.");
    }

    if (country?.cost_index >= 85) {
      candidates.push("Monthly costs leave less room for unexpected housing or healthcare changes.");
    }

    if (country?.cost_index <= 65) {
      candidates.push("Premium healthcare, housing quality, and expat-friendly services thin out faster outside the main hubs.");
    }

    if ((country?.healthcare_rating || 0) < 4.1) {
      candidates.push("Hospital quality, specialist access, and wait times vary more by city and region.");
    }

    if (summary?.need_reliable_internet && !String(country?.summary_notes || "").toLowerCase().includes("internet")) {
      candidates.push("Internet speed, backup options, and neighborhood reliability need verification before you rely on them day to day.");
    }

    return candidates;
  };

  const tightenPlanCardCopy = (text, country) => {
    if (!text) return text;

    const countryName = country?.name || "This country";
    const trimmed = text.trim();

    const rewrites = [
      {
        test: /^If healthcare is one of your main filters, .+?: (.+)$/i,
        value: (() => {
          const match = trimmed.match(/^If healthcare is one of your main filters, .+?: (.+)$/i);
          return match ? `Healthcare access is more reliable here: ${match[1]}` : trimmed;
        })()
      },
      {
        test: /^If healthcare matters heavily, .+? is more of a city-and-region decision: (.+)$/i,
        value: (() => {
          const match = trimmed.match(/^If healthcare matters heavily, .+? is more of a city-and-region decision: (.+)$/i);
          return match ? `Healthcare access varies much more by city and region here: ${match[1]}` : trimmed;
        })()
      },
      {
        test: /^.+? is worth considering only if (.+)$/i,
        value: (() => {
          const match = trimmed.match(/^.+? is worth considering only if (.+)$/i);
          return match ? `The case for ${countryName} is narrower: ${match[1]}` : trimmed;
        })()
      },
      {
        test: /^If this matters, (.+)$/i,
        value: (() => {
          const match = trimmed.match(/^If this matters, (.+)$/i);
          return match ? `This matters because ${match[1]}` : trimmed;
        })()
      }
    ];

    const rewrite = rewrites.find((entry) => entry.test.test(trimmed));
    if (rewrite) return rewrite.value;

    if (/^If you /i.test(trimmed)) {
      return `The practical reality here: ${trimmed.replace(/^If you /i, "")}`;
    }

    if (/^If /i.test(trimmed)) {
      const withoutIf = trimmed.replace(/^If /i, "");
      return withoutIf.charAt(0).toUpperCase() + withoutIf.slice(1);
    }

    return trimmed;
  };

  const tightenPlanPrecisionCopy = (text = "") => {
    if (!text) return text;

    return text
      .replace(/because it gives you a (?:distinct|stronger) [a-z]+ on ([^.]+)\.?/gi, (_, reason) => `because ${reason} is one of the clearest strengths here.`)
      .replace(/offers an [a-z]+ on ([^.]+)\.?/gi, (_, reason) => `stands out because ${reason} is one of the clearest strengths here.`)
      .replace(/gives you an [a-z]+ on ([^.]+)\.?/gi, (_, reason) => `looks stronger here on ${reason}.`)
      .replace(/healthcare confidence/gi, "healthcare access")
      .replace(/strong confidence/gi, "more reliable access")
      .replace(/with confidence/gi, "decisively")
      .replace(/confidence drops faster/gi, "reliable access thins out faster")
      .replace(/gives you another path through/gi, "offers another angle on")
      .replace(/gives you enough priority coverage to choose decisively/gi, "covers enough of your priorities to move ahead")
      .replace(/because it gives you/gi, "because it offers")
      .replace(/this helps you/gi, "")
      .replace(/this means/gi, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const tightenPlanTradeoffCopy = (text = "") => {
    const trimmed = text.trim();
    if (!trimmed) return trimmed;

    const rewrites = [
      [/Higher monthly costs require tighter budgeting\.?/i, "Monthly costs leave less room for unexpected housing or healthcare changes."],
      [/Higher monthly costs will require tighter budgeting(?: once housing and routine spending are priced in)?\.?/i, "Monthly costs leave less room for unexpected housing or healthcare changes."],
      [/Language barriers slow daily interactions here\.?/i, "Housing searches, healthcare appointments, and admin tasks require more local-language effort here."],
      [/Language barriers may slow down daily interactions\.?/i, "Housing searches, healthcare appointments, and admin tasks require more local-language effort here."],
      [/Healthcare access varies more by location\.?/i, "Hospital quality, specialist access, and wait times vary more by city and region."],
      [/The daily environment asks for more compromise here\.?/i, "Daily routines, pace, and service quality vary more once you narrow to a real city."],
      [/Overall stability is strong, but city-level safety still needs closer checking\.?/i, "Safety conditions vary more by city, so neighborhood choice and daily routines need verification."],
      [/Setup looks easier overall, but local setup still needs closer checking\.?/i, "Housing, paperwork, and daily logistics vary more by city and need verification."],
      [/.+ still needs closer checking on housing or local setup before it can stay near the top\.?/i, "Housing, paperwork, and daily logistics vary more by city and need verification."],
      [/needs closer checking/i, "Housing, paperwork, and daily logistics vary more by city and need verification."],
      [/some friction/i, "Housing, paperwork, and daily logistics create more friction once you narrow to a real city."],
      [/less favorable/i, "The practical setup is weaker once housing, paperwork, and daily logistics are priced in."],
      [/more pressure/i, "Monthly costs leave less room for unexpected housing or healthcare changes."],
    ];

    const rewrite = rewrites.find(([pattern]) => pattern.test(trimmed));
    return rewrite ? rewrite[1] : trimmed;
  };

  const buildPlanVerificationBullets = (country, tradeoffs = []) => {
    const locationVerificationByCountry = {
      ES: "Check whether regional cost differences and local regulations change your actual living setup.",
      PT: "Confirm that housing availability and pricing in your target city match your expected monthly budget.",
      FR: "Verify how much daily admin, healthcare, and housing require local-language comfort.",
      JP: "Confirm visa route requirements, documentation steps, and how long approval timelines take in practice.",
      GR: "Check how much healthcare, housing, and daily logistics depend on Athens or your exact island base.",
      UY: "Check how much healthcare, housing, and service access depend on Montevideo rather than the rest of the country.",
      default: "Check whether your likely base changes the cost, service access, or paperwork load more than the national picture suggests.",
    };

    const verificationMap = {
      affordability: "Price real rent, insurance, and monthly living costs before assuming the budget still holds.",
      healthcare: "Check the hospital network, private-care options, and real appointment times in the region you would actually use.",
      residency: "Confirm the exact residency route, document list, renewal rules, and processing time for the path you would actually use.",
      language: "Test how much housing, healthcare, banking, and paperwork can be handled without more local-language fluency.",
      infrastructure: "Stress-test internet speed, backup options, transport reliability, and daily logistics in the neighborhoods you would actually use.",
      lifestyle: "Compare daily pace, climate, and routine fit in the exact places you could realistically live.",
      work: "Confirm that your actual work pattern, income proof, and visa setup still fit the route this move would depend on.",
      stability: "Watch for policy shifts, regional volatility, and local safety differences that could change the practical risk quickly.",
      location: locationVerificationByCountry[country?.code] || locationVerificationByCountry.default,
    };

    const countrySpecificVerificationOverrides = {
      GR: {
        affordability: "Price Athens, Thessaloniki, and your likely island or mainland base separately before assuming Greece stays the cheaper Mediterranean answer.",
        healthcare: "Validate specialist access around Athens or the exact larger private hub you would rely on before assuming island or smaller-town living will feel simple.",
        residency: "Validate the exact residency route and renewal rhythm before assuming the lower-cost Mediterranean setup is easy to maintain.",
        infrastructure: "Stress-test ferries, flights, internet reliability, and year-round daily logistics from the exact mainland or island base you would actually use.",
        location: "Compare Athens, a mainland city, and any island shortlist side by side because the healthcare, housing, and convenience picture changes quickly.",
      },
      CR: {
        healthcare: "Check private hospital access from the exact Central Valley or coastal base you would actually use, because specialist care thins out faster outside the main corridor.",
        residency: "Confirm residency timing and document sequencing early, especially if you plan to live outside the main expat corridors.",
        infrastructure: "Stress-test road access, rainy-season logistics, and internet stability in the corridor you would really use day to day.",
        stability: "Screen neighborhood-level safety and local road conditions in the exact area you would choose rather than relying on the national reputation.",
        location: "Compare the Central Valley against your likely beach or lower-density areas before assuming the same safety, healthcare, and logistics picture.",
      },
      SG: {
        affordability: "Price housing, insurance, and routine city costs together before assuming efficiency alone justifies the Singapore premium.",
        healthcare: "Validate how your insurance, pass status, or employer-linked coverage would actually work before assuming the medical system is friction-free.",
        residency: "Confirm that your actual pass, business, or wealth route is genuinely open to you before treating Singapore as a viable relocation path.",
        lifestyle: "Check whether the compact, high-structure daily environment still feels right once cost and pace are added to the picture.",
      },
      NL: {
        affordability: "Price rent, commuting, and insurance together in the exact Dutch city you would target before assuming the systems advantage survives housing pressure.",
        healthcare: "Test whether the gatekeeper-first model and referral flow would still feel workable if you need specialist care quickly.",
        residency: "Confirm the exact work, partner, entrepreneur, or sponsorship route before treating the Netherlands as a low-friction move.",
        location: "Validate the actual housing search, transport pattern, and weather tradeoff in the city you could realistically secure.",
      },
    };

    Object.assign(verificationMap, countrySpecificVerificationOverrides[country?.code] || {});

    const countrySpecificFallbacks = {
      ES: [
        "Price rent, private insurance, and routine city costs before assuming the budget still works.",
        "Test how much healthcare, housing, and paperwork can be handled comfortably in Spanish."
      ],
      PT: [
        "Check current residency policy, document sequencing, and processing delays on the route you would actually use.",
        "Price long-stay rent, private insurance, and day-to-day costs in the exact cities still in play."
      ],
      GR: [
        "Check how much of your healthcare plan depends on Athens or the larger private-care hubs.",
        "Stress-test ferry, flight, internet, and daily logistics if island living is still part of the shortlist."
      ],
      UY: [
        "Check how much of your healthcare, admin, and housing plan depends on Montevideo rather than the rest of the country.",
        "Price rent, private care, and imported day-to-day costs before treating the budget as stable."
      ],
    };

    const selected = [];
    const selectedGroups = new Set();
    tradeoffs.forEach((tradeoff) => {
      const groups = getPlanIdeaGroups(tradeoff);
      groups.forEach((group) => {
        if (verificationMap[group] && !selectedGroups.has(group)) {
          selected.push(verificationMap[group]);
          selectedGroups.add(group);
        }
      });
    });

    if (!selected.length) {
      selected.push(...(countrySpecificFallbacks[country?.code] || [
        `Confirm the main residency route, monthly cost, and day-to-day setup you would actually rely on before keeping ${country?.name || "this option"} in active consideration.`,
        "Check the local housing, service access, and practical logistics in the specific place you would most likely choose."
      ]));
    }

    return selected.slice(0, 2);
  };

  const getResidencyComparisonNote = (country) => {
    const structuredNote = country?.plan_residency_comparison || RESIDENCY_COMPARISON_PROFILES[country?.code];
    if (structuredNote) {
      return {
        practicality: structuredNote.practicality,
        routeShape: structuredNote.routeShape || structuredNote.route_shape || "",
        watchout: structuredNote.watchout,
      };
    }

    const difficulty = country?.visa_difficulty || "Moderate";
    const language = (country?.language || "").toLowerCase();
    const costIndex = Number(country?.cost_index || 0);

    const practicality = difficulty === "Easy"
      ? "One of the more workable residency options in this shortlist if your paperwork is in order."
      : difficulty === "Hard"
        ? "More selective than it first appears, so it works best if you already fit a formal route."
        : "Workable, but more conditional than the easiest options in this shortlist.";

    const routeShape = language.includes("english")
      ? "Most likely through a work-, business-, or self-funded route rather than an open-ended lifestyle move."
      : "Most likely through a self-funded, work-linked, or business-linked route rather than a casual long-stay path.";

    const watchout = costIndex >= 85
      ? "High living costs make proof, buffer, and timing more important than they first appear."
      : difficulty === "Hard"
        ? "The lifestyle case may be stronger than the practical residency path if you do not already fit a clear category."
        : "The pathway stays more straightforward when your documentation and income profile are already easy to explain.";

    return { practicality, routeShape, watchout };
  };

  const budgetLabels = {
    low: "Low (Under $2,000/month)",
    medium: "Medium ($2,000–$4,000/month)",
    high: "High ($4,000–$7,000/month)",
    very_high: "Very high (Over $7,000/month)"
  };

  const timelineLabels = {
    asap: "ASAP (1-3 months)",
    "6_months": "Within 6 months",
    "1_year": "Within 1 year",
    "2_years": "Within 2 years",
    "2_years_or_more": "2 years or more"
  };

  const climateLabels = {
    warm: "Warm year-round",
    temperate: "Temperate with seasons",
    tropical: "Tropical",
    any: "No preference"
  };

  const healthcareLabels = {
    very_important: "Very important — top priority",
    important: "Important but not critical",
    moderate: "Moderate importance",
    not_priority: "Not a major priority"
  };

  const intendedStayLabels = {
    trial_exploratory_move: "Trial stay / exploratory move",
    "6_12_month_stay": "6–12 month stay",
    long_term_relocation: "Long-term relocation",
    split_time_between_countries: "Split time between countries"
  };

  const workAbroadLabels = {
    no: "No",
    remote_self_employed: "Yes — remote/self-employed",
    local_employment: "Yes — local employment",
    not_sure: "Not sure yet"
  };

  const expatCommunityLabels = {
    very_important: "Very important",
    important: "Important",
    moderate: "Moderately important",
    not_important: "Not important"
  };

  const familyLabels = {
    single: "Single",
    couple: "Couple (no children)",
    family_young: "Family with young children",
    family_older: "Family with older children",
    retired: "Retired / Empty nester"
  };

  const priorityLabels = {
    cost_of_living: "Long-term affordability",
    healthcare: "Healthcare quality",
    safety: "Safety & stability",
    climate: "Climate & weather",
    community: "Expat community",
    culture: "Daily pace & lifestyle fit",
    career: "Career opportunities",
    nature: "Nature & outdoors",
    taxes: "Tax efficiency",
    education: "Education quality",
    language_transition_ease: "Language comfort & ease of transition",
    residency_pathway_practicality: "Residency path realism",
    infrastructure_daily_convenience: "Infrastructure & daily convenience"
  };

  const shortlistRoleHintLabels = {
    lifestyle_fit: "lifestyle fit",
    healthcare_quality: "healthcare",
    residency_path: "residency ease",
    cost_of_living_fit: "budget fit",
    ease_of_relocation: "simplicity"
  };

  const getCountryRoleMeta = (country, index, countries) => {
    const leadCountry = countries[0];
    const leadComparisons = Object.entries(snapshotMetricLabels)
      .map(([key, label]) => ({
        key,
        label,
        delta: Number(country?.metrics?.[key] || 0) - Number(leadCountry?.metrics?.[key] || 0)
      }))
      .sort((a, b) => b.delta - a.delta);
    const bestRelativeEdge = leadComparisons.find(({ delta }) => delta > 0);
    const strongestMetric = getSnapshotMetricEntries(country)[0];

    if (index === 0) {
      return {
        label: "Top contender",
        summary: `${country.name} looks strongest across the main practical factors in this shortlist.`
      };
    }

    if (index === 1) {
      return {
        label: "Strong alternative",
        summary: bestRelativeEdge
          ? `${country.name} is stronger than the lead on ${bestRelativeEdge.label}.`
          : `${country.name} is the strongest backup option right now.`
      };
    }

    if ((country?.metrics?.lifestyle_fit || 0) >= 88 && (country?.metrics?.lifestyle_fit || 0) >= (country?.metrics?.residency_path || 0) + 5) {
      return {
        label: "Lifestyle-led alternative",
        summary: `${country.name} feels stronger day to day than the easier paperwork option.`
      };
    }

    if ((country?.metrics?.healthcare_quality || 0) >= 88 || (country?.metrics?.ease_of_relocation || 0) >= 88) {
      return {
        label: "Systems-forward comparison",
        summary: `${country.name} is stronger on healthcare, daily systems, or move setup.`
      };
    }

    if ((country?.cost_index || 0) >= 90) {
      return {
        label: "Higher-cost option",
        summary: `${country.name} costs more, but it may offer stronger stability, services, or overall quality of life.`
      };
    }

    if (strongestMetric) {
      return {
        label: `Worth comparing if ${shortlistRoleHintLabels[strongestMetric.key] || strongestMetric.label} matters more`,
        summary: `${country.name} stands out most on ${strongestMetric.label}, even if the rest of the picture is less complete.`
      };
    }

    return {
      label: "Worth comparing",
      summary: `${country.name} still offers enough to deserve a closer look.`
    };
  };

  const getSpecificPlanRoleSummary = (country, index, countries) => {
    const fallbackCountryRole = getCountryRoleMeta(country, index, countries);
    return country?.plan_role_summary || country?.role_summary || fallbackCountryRole.summary || null;
  };

  const getTimelinePhaseMeta = (phaseName) => {
    const phase = (phaseName || "").toLowerCase();

    if (phase.includes("clarify")) {
      return { Icon: Search, accentClassName: "bg-stone-100 text-stone-700", label: "Clarify" };
    }
    if (phase.includes("narrow")) {
      return { Icon: Search, accentClassName: "bg-stone-100 text-stone-700", label: "Narrow" };
    }
    if (phase.includes("resolve")) {
      return { Icon: Compass, accentClassName: "bg-amber-50 text-amber-700", label: "Resolve" };
    }
    if (phase.includes("check")) {
      return { Icon: Compass, accentClassName: "bg-amber-50 text-amber-700", label: "Check" };
    }
    if (phase.includes("pressure-test")) {
      return { Icon: Compass, accentClassName: "bg-amber-50 text-amber-700", label: "Test" };
    }
    if (phase.includes("validate")) {
      return { Icon: Compass, accentClassName: "bg-amber-50 text-amber-700", label: "Validate" };
    }
    if (phase.includes("confirm")) {
      return { Icon: CheckCircle2, accentClassName: "bg-emerald-50 text-emerald-700", label: "Confirm" };
    }
    if (phase.includes("commit")) {
      return { Icon: CheckCircle2, accentClassName: "bg-emerald-50 text-emerald-700", label: "Commit" };
    }
    if (phase.includes("likely winner") || phase.includes("become specific")) {
      return { Icon: CheckCircle2, accentClassName: "bg-emerald-50 text-emerald-700", label: "Decide" };
    }
    if (phase.includes("preparation") || phase.includes("prepare")) {
      return { Icon: MapPinned, accentClassName: "bg-sky-50 text-sky-700", label: "Prepare" };
    }
    if (phase.includes("shift")) {
      return { Icon: MapPinned, accentClassName: "bg-sky-50 text-sky-700", label: "Prepare" };
    }
    if (phase.includes("choose")) {
      return { Icon: CheckCircle2, accentClassName: "bg-emerald-50 text-emerald-700", label: "Choose" };
    }
    if (phase.includes("move into execution") || phase.includes("prepare and execute")) {
      return { Icon: PlaneTakeoff, accentClassName: "bg-orange-50 text-orange-700", label: "Execute" };
    }
    if (phase.includes("prepare")) {
      return { Icon: MapPinned, accentClassName: "bg-sky-50 text-sky-700", label: "Prepare" };
    }

    if (phase.includes("readiness") || phase.includes("execute")) {
      return { Icon: PlaneTakeoff, accentClassName: "bg-orange-50 text-orange-700", label: "Execute" };
    }

    return { Icon: PlaneTakeoff, accentClassName: "bg-orange-50 text-orange-700", label: "Move" };
  };

  const formatLabel = (value, labels) => {
    if (!value) return "Not specified";
    return labels[value] || value.toString().replace(/_/g, " ");
  };

  const formatThemeList = (items) => {
    const filtered = items.filter(Boolean);
    if (!filtered.length) return "fit, feasibility, and relocation practicality";
    if (filtered.length === 1) return filtered[0];
    if (filtered.length === 2) return `${filtered[0]} and ${filtered[1]}`;
    return `${filtered.slice(0, -1).join(", ")}, and ${filtered[filtered.length - 1]}`;
  };

  const getPriorityNarrativeLabel = (priority) => {
    if (priority === "culture") return "day-to-day pace";
    return (priorityLabels[priority] || priority.toString().replace(/_/g, " ")).toLowerCase();
  };

  const snapshotMetricLabels = {
    lifestyle_fit: "day-to-day fit",
    healthcare_quality: "healthcare access",
    residency_path: "residency practicality",
    cost_of_living_fit: "cost fit",
    ease_of_relocation: "execution ease"
  };

  const getSnapshotMetricEntries = (country) => (
    Object.entries(snapshotMetricLabels)
      .map(([key, label]) => ({
        key,
        label,
        value: Number(country?.metrics?.[key] || 0)
      }))
      .sort((a, b) => b.value - a.value)
  );

  const getSnapshotCountryStrengths = (country, limit = 2) => (
    getSnapshotMetricEntries(country)
      .filter(({ value }) => value > 0)
      .slice(0, limit)
      .map(({ label }) => label)
  );

  const buildLeadingCountryFallback = (countries) => {
    const leading = countries[0];
    if (!leading) return null;

    const strengths = getSnapshotCountryStrengths(leading);
    const topPriority = planSummary?.priorities?.[0];
    const secondPriority = planSummary?.priorities?.[1];
    const topPriorityLabel = topPriority ? getPriorityNarrativeLabel(topPriority) : null;
    const secondPriorityLabel = secondPriority ? getPriorityNarrativeLabel(secondPriority) : null;
    return {
      name: leading.name,
      code: leading.code,
      summary: topPriorityLabel
        ? `${leading.name} looks strongest on ${topPriorityLabel}.${secondPriorityLabel ? ` It also reads well on ${secondPriorityLabel}.` : ""}${strengths.length ? ` Other clear strengths: ${formatThemeList(strengths.slice(0, 2))}.` : ""}`
        : strengths.length
          ? `${leading.name} looks strongest on ${formatThemeList(strengths)}.`
          : `${leading.name} lines up best with the priorities you ranked highest.`
    };
  };

  const buildStrongestAlternativeFallback = (countries) => {
    const leading = countries[0];
    const alternative = countries[1];

    if (!alternative) return null;

    const topPriority = planSummary?.priorities?.[0];
    const secondPriority = planSummary?.priorities?.[1];
    const topPriorityLabel = topPriority ? getPriorityNarrativeLabel(topPriority) : null;
    const secondPriorityLabel = secondPriority ? getPriorityNarrativeLabel(secondPriority) : null;

    const alternativeEdge = getSnapshotMetricEntries(alternative)
      .map(({ key, label }) => ({
        key,
        label,
        delta: Number(alternative?.metrics?.[key] || 0) - Number(leading?.metrics?.[key] || 0)
      }))
      .find(({ delta }) => delta > 0);

    if (alternativeEdge) {
      return {
        name: alternative.name,
        code: alternative.code,
        summary: topPriorityLabel
          ? `${alternative.name} looks especially strong on ${topPriorityLabel}.${secondPriorityLabel ? ` It also reads well on ${secondPriorityLabel}.` : ""}${alternativeEdge.label ? ` Another point in its favor: ${alternativeEdge.label}.` : ""}`
          : `${alternative.name} comes through more clearly on ${alternativeEdge.label}, even though ${leading?.name || "the strongest option"} still looks stronger overall.`
      };
    }

    const strengths = getSnapshotCountryStrengths(alternative);
    return {
      name: alternative.name,
      code: alternative.code,
      summary: topPriorityLabel
        ? `${alternative.name} remains competitive on ${topPriorityLabel}.${secondPriorityLabel ? ` It also reads well on ${secondPriorityLabel}.` : ""}${strengths.length ? ` Other clear strengths: ${formatThemeList(strengths.slice(0, 2))}.` : ""}`
        : strengths.length
          ? `${alternative.name} still looks strong on ${formatThemeList(strengths)}.`
          : `${alternative.name} is the strongest backup option right now.`
    };
  };

  const buildMainDecisionTensionFallback = (countries) => {
    const leading = countries[0];
    const alternative = countries[1];

    if (!leading && !alternative) {
      return "Look for the option that combines your top priorities with a realistic setup.";
    }

    if (leading && !alternative) {
      return `Check whether ${leading.name} still looks strong once you test real cities, housing, and residency steps.`;
    }

    const leadingMetrics = leading?.metrics || {};
    const alternativeMetrics = alternative?.metrics || {};
    const metricDiffs = Object.entries(snapshotMetricLabels).map(([key, label]) => ({
      key,
      label,
      delta: Number(leadingMetrics[key] || 0) - Number(alternativeMetrics[key] || 0)
    }));

    const leadingEdge = [...metricDiffs]
      .filter(({ delta }) => delta > 0)
      .sort((a, b) => b.delta - a.delta)[0];
    const alternativeEdge = [...metricDiffs]
      .filter(({ delta }) => delta < 0)
      .sort((a, b) => a.delta - b.delta)[0];

    const getLeadingTradeoffPhrase = () => {
      if (Number(leadingMetrics.residency_path || 0) > Number(alternativeMetrics.residency_path || 0) && Number(leadingMetrics.cost_of_living_fit || 0) > Number(alternativeMetrics.cost_of_living_fit || 0)) {
        return "practicality and affordability";
      }
      if (Number(leadingMetrics.residency_path || 0) > Number(alternativeMetrics.residency_path || 0)) {
        return "practicality";
      }
      if (Number(leadingMetrics.cost_of_living_fit || 0) > Number(alternativeMetrics.cost_of_living_fit || 0)) {
        return "affordability";
      }
      return leadingEdge?.label || "the current priority picture";
    };

    const getAlternativeTradeoffPhrase = () => {
      const preferredAlternativeEdge = getSnapshotMetricEntries(alternative)
        .map(({ key, label }) => ({
          key,
          label,
          delta: Number(alternativeMetrics[key] || 0) - Number(leadingMetrics[key] || 0)
        }))
        .find(({ delta }) => delta > 0);
      return preferredAlternativeEdge?.label || alternativeEdge?.label || "its narrower strengths";
    };

    const leadingSafetyLevel = getSafetyLevel(leading);
    const alternativeSafetyLevel = getSafetyLevel(alternative);
    if (Math.abs(leadingSafetyLevel - alternativeSafetyLevel) >= 1) {
      if (leadingSafetyLevel < alternativeSafetyLevel) {
        return `${leading.name} looks steadier day to day. ${alternative.name} looks stronger on ${getAlternativeTradeoffPhrase()}.`;
      }
      if (alternativeSafetyLevel < leadingSafetyLevel) {
        return `${alternative.name} looks steadier day to day. ${leading.name} looks stronger on ${getLeadingTradeoffPhrase()}.`;
      }
    }

    if (leadingEdge && alternativeEdge) {
      return `${leading.name} looks stronger on ${getLeadingTradeoffPhrase()}. ${alternative.name} looks stronger on ${getAlternativeTradeoffPhrase()}.`;
    }

    if (leadingEdge) {
      return `${leading.name} looks stronger on ${getLeadingTradeoffPhrase()}. ${alternative.name} needs a clearer edge elsewhere to catch up.`;
    }

    const alternativeStrength = getSnapshotCountryStrengths(alternative, 1)[0];
    if (alternativeStrength) {
      return `${leading.name} still has the broader overall case. ${alternative.name} looks stronger on ${alternativeStrength}.`;
    }

    return `${leading.name} still has the broader overall case. ${alternative.name} needs a clearer edge on one or two factors to overtake it.`;
  };

  const isRetiredProfile = (summary) => {
    const occupation = (summary?.occupation || "").toLowerCase();
    return summary?.family_status === "retired" || occupation.includes("retir") || occupation.includes("pension") || occupation.includes("empty nester");
  };

  const buildPlanSnapshotFallback = (summary, countryNames) => {
    const workIntent = summary?.work_abroad_intent;
    const retiredProfile = isRetiredProfile(summary);
    const overviewThemes = [];
    const addTheme = (theme) => {
      if (theme && !overviewThemes.includes(theme)) overviewThemes.push(theme);
    };

    if (workIntent === "remote_self_employed") {
      addTheme("remote-work practicality");
      if (summary?.need_reliable_internet) addTheme("reliable infrastructure");
      addTheme("realistic residency planning");
    } else if (workIntent === "local_employment") {
      addTheme("work-linked residency practicality");
      addTheme("day-to-day livability");
      addTheme("execution realism");
    } else {
      addTheme("lifestyle fit");
      addTheme("realistic residency pathways");
    }

    if (["very_important", "important"].includes(summary?.healthcare_importance)) addTheme("healthcare access");
    if (["low", "medium"].includes(summary?.budget)) addTheme("budget sustainability");
    if (summary?.intended_length_of_stay === "long_term_relocation") addTheme("long-term practicality");
    if (["very_important", "important"].includes(summary?.expat_community_importance)) addTheme("social landing ease");
    if (summary?.timeline === "asap") addTheme("timeline realism");

    const rankedPriorityLabels = (summary?.priorities || [])
      .slice(0, 3)
      .map((priority) => getPriorityNarrativeLabel(priority));
    const overview = rankedPriorityLabels.length
      ? `${countryNames || "Your top countries"} stay strongest first on ${rankedPriorityLabels[0]}${rankedPriorityLabels.length > 1 ? `, with ${formatThemeList(rankedPriorityLabels.slice(1, 3))} also separating the shortlist` : ""}.`
      : `${countryNames || "Your top countries"} keep the clearest decision picture around ${formatThemeList(overviewThemes.slice(0, 3))}.`;

    if (workIntent === "remote_self_employed") {
      return {
        overview,
        keyConstraint: "The main thing to watch is balancing remote-income proof, reliable infrastructure, and country-specific residency rules.",
        strategicInsight: "The clearest choice usually comes from countries where infrastructure, residency fit, and day-to-day living all support the same work setup."
      };
    }

    if (workIntent === "local_employment") {
      return {
        overview,
        keyConstraint: "The main thing to watch is whether local work plans, residency options, and your timing line up cleanly.",
        strategicInsight: "The clearest choice usually comes from countries where local work plans and residency options support the same path."
      };
    }

    if (retiredProfile) {
      let keyConstraint = "The main thing to watch is whether residency remains practical for your timeline and the kind of lifestyle you want to sustain long term.";
      if (["low", "medium"].includes(summary?.budget) && ["very_important", "important"].includes(summary?.healthcare_importance)) {
        keyConstraint = "The main thing to watch is whether residency stays realistic for your budget while still keeping healthcare access reliable enough for daily life.";
      } else if (["very_important", "important"].includes(summary?.healthcare_importance)) {
        keyConstraint = "The main thing to watch is whether residency remains practical while still keeping healthcare access reliable enough for daily life.";
      } else if (["low", "medium"].includes(summary?.budget)) {
        keyConstraint = "The main thing to watch is whether residency stays realistic without stretching your long-term budget.";
      }
      return {
        overview,
        keyConstraint,
        strategicInsight: "The clearest choice usually comes from countries where residency fit, healthcare access, and everyday costs all hold up together."
      };
    }

    if (workIntent === "no") {
      return {
        overview,
        keyConstraint: "The main thing to watch is whether residency remains practical without relying on local employment, while still fitting your budget and timeline.",
        strategicInsight: "The clearest choice usually comes from countries where non-work-based residency options, budget fit, and day-to-day practicality all hold up together."
      };
    }

    return {
      overview,
      keyConstraint: "The main thing to clarify is whether this move will be supported by your own finances, remote income, or local employment.",
      strategicInsight: "Once that support path is clear, the shortlist usually narrows much faster."
    };
  };

  const buildDecisionSensitivitiesFallback = (summary, countries) => {
    if (!countries.length) {
      return [
        "City-level details could still weaken the option that looks strongest right now.",
        "Recheck the decision if the practical side of the move starts to feel weaker than it does now."
      ];
    }

    const lead = countries[0];
    const alternative = countries[1];
    const topPriorityText = formatThemeList(
      (summary?.priorities || [])
        .slice(0, 2)
        .map((priority) => getPriorityNarrativeLabel(priority))
    );

    const bullets = [];
    if (topPriorityText && alternative?.name) {
      bullets.push(`${alternative.name} stays relevant because of its strength on ${topPriorityText}.`);
    }

    if (alternative && Math.abs(getSafetyLevel(lead) - getSafetyLevel(alternative)) >= 1) {
      if (getSafetyLevel(lead) < getSafetyLevel(alternative)) {
        bullets.push(`${lead.name} strengthens if day-to-day stability starts to matter more than cost or lifestyle upside.`);
      } else {
        bullets.push(`${lead.name} stays viable only if the higher-caution signal still feels manageable after city-level checking.`);
      }
    }

    bullets.push(`${alternative?.name || "The other country"} becomes more live if ${lead.name} weakens on real monthly cost, residency practicality, or day-to-day fit.`);

    if (summary?.work_abroad_intent === "remote_self_employed") {
      bullets.push(`Reopen the comparison if remote income proof, infrastructure, or residency fit feel harder in practice than they do on paper.`);
    } else if (summary?.work_abroad_intent === "local_employment") {
      bullets.push(`Reopen the comparison if work timing, employer reality, or the residency path feel harder in practice than expected.`);
    } else {
      bullets.push(`Reopen the comparison if the practical side of the move feels less workable in real life than it does now.`);
    }

    return bullets.slice(0, 4);
  };

  const tightenDecisionSensitivityLine = (line, countries) => {
    if (!line) return line;

    const lead = countries?.[0]?.name || "the lead";
    const alternative = countries?.[1]?.name || "the backup option";

    const rewrites = [
      {
        test: /^If the lead still looks good once you check real city-level details/i,
        value: "City-level details could still weaken the option that looks strongest right now."
      },
      {
        test: /^If the practical side of the move starts to feel less workable/i,
        value: "Recheck the decision if the practical side of the move starts to feel weaker than it does now."
      },
      {
        test: /^If day-to-day stability starts to matter more/i,
        value: `${lead} strengthens if day-to-day stability starts to matter more than cost or lifestyle upside.`
      },
      {
        test: /^If you decide the higher-caution signal is manageable/i,
        value: `${lead} stays viable only if the higher-caution signal still feels manageable after city-level checking.`
      },
      {
        test: /^If the higher-caution signal in /i,
        value: `${lead} stays viable only if the higher-caution signal still feels manageable after city-level checking.`
      },
      {
        test: /^If real monthly costs end up tighter than you expect, .+ is more likely to look stronger, not weaker\.?$/i,
        value: `${lead} strengthens if real monthly costs end up tighter than expected.`
      },
      {
        test: /^If real monthly costs end up tighter than you expect, .+ could start to look more attractive again\.?$/i,
        value: `${alternative} becomes more attractive if real monthly costs end up tighter than expected.`
      },
      {
        test: /^If .+ starts to matter more to you in real life than it does on paper, .+ could still make the recommendation less clear-cut\.?$/i,
        value: (() => {
          const match = line.match(/^If (.+) starts to matter more to you in real life than it does on paper, (.+) could still make the recommendation less clear-cut\.?$/i);
          if (!match) return line;
          const factor = match[1];
          const country = match[2];
          return `${country} can reopen the decision if ${factor} starts carrying more weight in real life.`;
        })()
      },
      {
        test: /^If .+ does not hold up once you look more closely at /i,
        value: line
          .replace(/^If /i, "")
          .replace(/ does not hold up once you look more closely at /i, " weakens once you test ")
          .replace(/ is worth another look before moving ahead\.?$/i, " is worth revisiting.")
      },
      {
        test: /^If .+ stops holding up on /i,
        value: line
          .replace(/^If /i, "")
          .replace(/ stops holding up on /i, " weakens on ")
          .replace(/ is worth another look before you move ahead\.?$/i, " becomes worth revisiting.")
      },
      {
        test: /^If remote income, infrastructure, or residency fit /i,
        value: "Reopen the comparison if remote income proof, infrastructure, or residency fit feel harder in practice than they do on paper."
      },
      {
        test: /^If work timing, employer reality, or the residency path /i,
        value: "Reopen the comparison if work timing, employer reality, or the residency path feel harder in practice than expected."
      },
      {
        test: /^If the non-work path, healthcare fit, or monthly cost /i,
        value: "Reopen the comparison if the non-work path, healthcare fit, or monthly cost feel weaker in practice than they do now."
      },
      {
        test: /^If the practical path behind the move /i,
        value: "Reopen the comparison if the practical path behind the move feels weaker in practice than it does now."
      }
    ];

    const rewrite = rewrites.find((entry) => entry.test.test(line));
    return rewrite ? rewrite.value : line.replace(/^If\s+/i, "");
  };

  const buildDecisionSensitivityOverrides = (summary, countries) => {
    if (!countries?.length) {
      return buildDecisionSensitivitiesFallback(summary, countries)
        .map((item) => tightenDecisionSensitivityLine(item, countries))
        .slice(0, 3);
    }

    const lead = countries[0];
    const alternative = countries[1] || countries[0];
    const pricierCountry = countries[1]
      ? ((Number(lead?.cost_index || 0) >= Number(alternative?.cost_index || 0)) ? lead : alternative)
      : lead;
    const steadierCountry = countries[1]
      ? ((getSafetyLevel(lead) <= getSafetyLevel(alternative)) ? lead : alternative)
      : lead;
    const validationPressureCountry = countries[1]
      ? (((getVisaDifficultyRank(lead) - getHealthcareConfidenceRank(lead)) >= (getVisaDifficultyRank(alternative) - getHealthcareConfidenceRank(alternative))) ? lead : alternative)
      : lead;

    return [
      `If real monthly costs run higher than expected, ${pricierCountry.name} becomes harder to justify.`,
      `If day-to-day stability matters more than lifestyle upside, ${steadierCountry.name} becomes more convincing.`,
      `Residency steps and healthcare access still need city-level checking before ${validationPressureCountry.name === lead.name ? lead.name : `${lead.name} or ${validationPressureCountry.name}` } feels ready to move forward.`
    ];
  };

  const buildNextStepsFallback = (summary) => {
    if (summary?.work_abroad_intent === "remote_self_employed") {
      return [
        "Cut the shortlist to the countries that still hold up on residency rules, daily work setup, and practical livability",
        "Compare the strongest option and the backup option at the city level on housing, healthcare, and work reliability",
        "Move forward with one country only after the main tradeoff is clear and the case still holds"
      ];
    }

    if (summary?.work_abroad_intent === "local_employment") {
      return [
        "Cut the shortlist to the countries where local work plans, residency options, and timing still line up cleanly",
        "Compare the strongest option and the backup option on likely cities, hiring reality, and the practical residency path",
        "Move forward with one country only after the main tradeoff is clear and one path still looks strongest"
      ];
    }

    if (isRetiredProfile(summary)) {
      return [
        "Cut the shortlist to the countries that still hold up on residency practicality, healthcare access, and budget",
        "Compare the strongest option and the backup option on likely cities, monthly costs, and the practical residency path",
        "Move forward with one country only after the main tradeoff is clear and one country still feels stronger for the life you want"
      ];
    }

    if (["very_important", "important"].includes(summary?.healthcare_importance)) {
      return [
        "Cut the shortlist to the countries that still hold up on residency realism and healthcare access",
        "Compare the strongest option and the backup option on healthcare access, likely cities, and everyday costs",
        "Move forward with one country only after the main tradeoff is clear and one option still feels stronger"
      ];
    }

    return [
      "Cut the shortlist to the countries that still look strongest on fit, practicality, and residency realism",
      "Compare the strongest option and the backup option by reviewing likely cities, costs, and the practical residency path",
      "Move forward with one country only after the main tradeoff is clear and one option still holds up more clearly"
    ];
  };

  const tightenNextStepLine = (line) => {
    if (!line) return line;

    const rewrites = [
      {
        test: /^Cut the shortlist to the countries that still hold up once /i,
        value: line
          .replace(/still hold up once (.+?) are weighed together/i, "still hold up on $1")
          .replace(/still hold up once (.+?) is weighed together/i, "still hold up on $1")
          .replace(/ — right now, that means keeping .+ as (the )?live comparison\.?$/i, ".")
      },
      {
        test: /^Cut the shortlist to the countries that still look strongest once you weigh /i,
        value: line
          .replace(/still look strongest once you weigh (.+?) together/i, "still look strongest on $1")
          .replace(/ — right now, that means keeping .+ as your live comparison\.?$/i, ".")
      },
      {
        test: /^Validate the lead option against its closest alternative by comparing /i,
        value: line.replace(/^Validate the lead option against its closest alternative by comparing /i, "Compare the strongest option and the backup option on ")
      },
      {
        test: /^Check the lead option against its closest alternative by looking at /i,
        value: line.replace(/^Check the lead option against its closest alternative by looking at /i, "Compare the strongest option and the backup option on ")
      },
      {
        test: /^Move forward only once the main tradeoff between /i,
        value: line
          .replace(/^Move forward only once the main tradeoff between /i, "Move forward with one country only after the main tradeoff between ")
          .replace(/ is resolved and one country still looks clearly stronger\.?$/i, " is clear and one country still looks clearly stronger.")
          .replace(/ is resolved and one country still offers the clearest path\.?$/i, " is clear and one country still offers the clearest path.")
          .replace(/ is resolved and one country still feels clearly stronger for the life you want\.?$/i, " is clear and one country still feels clearly stronger for the life you want.")
          .replace(/ is resolved and one country still feels clearly stronger\.?$/i, " is clear and one country still feels clearly stronger.")
      },
      {
        test: /^Move ahead only once the main tradeoff between /i,
        value: line
          .replace(/^Move ahead only once the main tradeoff between /i, "Move forward with one country only after the main tradeoff between ")
          .replace(/ feels resolved and one country still looks more clearly right for you\.?$/i, " is clear and one country still looks more clearly right for you.")
      }
    ];

    const rewrite = rewrites.find((entry) => entry.test.test(line));
    return tightenPlanPrecisionCopy(rewrite ? rewrite.value : line);
  };

  const shortlistedCountries = plan?.shortlisted_countries || [];
  const topCountryNames = shortlistedCountries.slice(0, 5).map((c) => c.name).join(", ");
  const planSummary = plan?.summary || {};
  const planPaceSignalState = getPaceSignalState(planSummary);
  const planRemoteDecisionContrast = getRemoteDecisionContrast(shortlistedCountries.slice(0, 2), planSummary);
  const planReasoningMap = useMemo(
    () => buildCountryReasoningMap(shortlistedCountries, planSummary, { surface: "plan", fitLimit: 2, tradeoffLimit: 3 }),
    [shortlistedCountries, planSummary]
  );
  const planSectionReasoningMap = useMemo(
    () => shortlistedCountries.reduce((acc, country, index) => {
      const reasoning = planReasoningMap[country.code] || {};
      const fallbackCountryRole = getCountryRoleMeta(country, index, shortlistedCountries);
      const roleSummary = country.plan_role_summary || country.role_summary || fallbackCountryRole.summary;
      const fitCandidates = reasoning.fitReasons?.length ? reasoning.fitReasons : getStrengths(country);
      const highlights = filterDistinctPlanIdeas(fitCandidates, [roleSummary], 2);
      const blockedIdeas = [roleSummary, ...highlights];
      const hasGovernedTradeoffs = Array.isArray(country.processed_tradeoffs);
      const backendTradeoffs = hasGovernedTradeoffs
        ? sanitizeRenderableList(country.processed_tradeoffs, 3)
        : [];
      const hasGovernedVerification = Array.isArray(country.tradeoff_verification_points);
      const backendVerificationBullets = hasGovernedVerification
        ? sanitizeRenderableList(country.tradeoff_verification_points, 2)
        : [];
      const tradeoffCandidates = [
        ...(reasoning.tradeoffReasons || []),
        ...buildPlanCautionCandidates(country, planSummary),
        ...getTradeoffs(country),
      ];
      const fallbackTradeoffs = hasGovernedTradeoffs
        ? backendTradeoffs
        : (() => {
          const tradeoffs = finalizeDistinctTradeoffs(
            filterDistinctPlanIdeas(tradeoffCandidates, blockedIdeas, 3)
              .map((idea) => qualifyTradeoffAgainstFit(idea, highlights, roleSummary))
              .map((idea) => tightenPlanTradeoffCopy(idea)),
            blockedIdeas,
            3,
          );

          if (tradeoffs.length) {
            return sanitizeRenderableList(tradeoffs, 3);
          }

          return sanitizeRenderableList(
            finalizeDistinctTradeoffs(
            filterDistinctPlanIdeas(buildPlanCautionCandidates(country, planSummary), blockedIdeas, 3)
              .map((idea) => qualifyTradeoffAgainstFit(idea, highlights, roleSummary))
              .map((idea) => tightenPlanTradeoffCopy(idea)),
            blockedIdeas,
            3,
          ), 3);
        })();
      const verificationBullets = hasGovernedVerification
        ? backendVerificationBullets
        : sanitizeRenderableList(buildPlanVerificationBullets(country, fallbackTradeoffs), 2);
      const backendStrategicBullets = Array.isArray(country.plan_implication_bullets)
        ? sanitizeRenderableList(country.plan_implication_bullets, 2)
        : [];
      const strategicBullets = backendStrategicBullets.length
        ? backendStrategicBullets
        : sanitizeRenderableList([
          verificationBullets[0] ? `Validate next: ${verificationBullets[0]}` : null,
          fallbackTradeoffs[0] ? `Watch later: ${fallbackTradeoffs[0]}` : null,
        ], 2);
      const teaser = [
        fallbackTradeoffs[0] || null,
        verificationBullets[0] ? `Verify next: ${verificationBullets[0]}` : null,
      ].filter(Boolean).join(" • ");

      acc[country.code] = {
        highlights,
        strategicBullets,
        tradeoffs: fallbackTradeoffs,
        verificationBullets,
        teaser,
      };
      return acc;
    }, {}),
    [shortlistedCountries, planReasoningMap, planSummary]
  );
  const snapshotFallback = buildPlanSnapshotFallback(planSummary, topCountryNames);
  const leadingCountrySnapshot =
    plan?.snapshot?.leading_country?.name
      ? plan.snapshot.leading_country
      : buildLeadingCountryFallback(shortlistedCountries);
  const strongestAlternativeSnapshot =
    plan?.snapshot?.strongest_alternative?.name
      ? plan.snapshot.strongest_alternative
      : buildStrongestAlternativeFallback(shortlistedCountries);
  const mainDecisionTension =
    plan?.snapshot?.main_decision_tension ||
    buildMainDecisionTensionFallback(shortlistedCountries);
  const planConstraint =
    plan?.snapshot?.key_constraint ||
    snapshotFallback.keyConstraint;
  const planNarrowingInsight =
    plan?.snapshot?.strategic_insight ||
    snapshotFallback.strategicInsight;
  const decisionSensitivities = buildDecisionSensitivityOverrides(planSummary, shortlistedCountries);
  const nextSteps =
    plan?.next_steps?.length
      ? plan.next_steps.map((item) => tightenNextStepLine(item))
      : buildNextStepsFallback(planSummary).map((item) => tightenNextStepLine(item));
  const sortedTimeline = [...(plan?.timeline || [])].sort((a, b) => (a.month || 0) - (b.month || 0));
  const totalTimelineMonths = {
    asap: 3,
    "6_months": 6,
    "1_year": 12,
    "2_years": 24,
    "2_years_or_more": 30,
  }[planSummary.timeline] || sortedTimeline[sortedTimeline.length - 1]?.month || 12;
  const topPriorityLabels = (plan?.summary?.priorities || []).map(
    (priority) => priorityLabels[priority] || priority.toString().replace(/_/g, " ")
  );
  const topPriorityNarrativeLabels = (plan?.summary?.priorities || []).map(
    (priority) => getPriorityNarrativeLabel(priority)
  );
  const planPriorityFocusLine = topPriorityLabels.length
    ? `The strongest separation here comes from ${topPriorityNarrativeLabels[0]}${topPriorityNarrativeLabels.length > 1 ? `, with ${formatThemeList(topPriorityNarrativeLabels.slice(1, 2))} also helping sort the shortlist.` : "."}`
    : null;
  const activeComparisonCountries = shortlistedCountries.slice(0, 2);
  const nextStepsComparisonLabel = shortlistedCountries.slice(0, 2).map((country) => country.name).join(" and ") || "your two strongest options";
  const profileItems = [
    {
      label: "Budget range",
      value: formatLabel(plan?.summary?.budget, budgetLabels)
    },
    {
      label: "Timeline",
      value: formatLabel(plan?.summary?.timeline, timelineLabels)
    },
    {
      label: "Intended stay",
      value: formatLabel(plan?.summary?.intended_length_of_stay, intendedStayLabels)
    },
    {
      label: "Climate preference",
      value: formatLabel(plan?.summary?.preferred_climate, climateLabels)
    },
    {
      label: "Healthcare importance",
      value: formatLabel(plan?.summary?.healthcare_importance, healthcareLabels)
    },
    {
      label: "Family considerations",
      value: formatLabel(plan?.summary?.family_status, familyLabels)
    },
    {
      label: "Work abroad",
      value: formatLabel(plan?.summary?.work_abroad_intent, workAbroadLabels)
    },
    {
      label: "Reliable internet",
      value: typeof plan?.summary?.need_reliable_internet === "boolean" ? (plan.summary.need_reliable_internet ? "Yes" : "No") : "Not specified"
    },
    {
      label: "Expat community",
      value: formatLabel(plan?.summary?.expat_community_importance, expatCommunityLabels)
    },
    {
      label: "Current country",
      value: plan?.summary?.current_country || "Not specified"
    },
    {
      label: "Citizenship",
      value: plan?.summary?.citizenship || "Not specified"
    }
  ].filter((item) => item.value);
  const visibleMotivationLabels = (plan?.summary?.motivations || [])
    .map((motivation) => getMotivationLabel(motivation) || motivation.replace(/_/g, " "))
    .filter(Boolean)
    .slice(0, 3);
  const visibleLifestyleRefinerLabels = (plan?.summary?.lifestyle_preferences || [])
    .map((preference) => LIFE_REFINER_LABELS[preference] || preference.replace(/_/g, " "))
    .filter(Boolean)
    .slice(0, 3);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Generating your personalized plan...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground">No plan found</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-4">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const planAccess = plan?.plan_access || user?.plan_access || {};
  const refreshStatusTitle = planAccess.requires_new_purchase_for_refresh
    ? "This plan is based on an earlier paid assessment."
    : planAccess.included_refresh_available
      ? "Your paid Blueprint still includes one free refresh."
      : "Your current Blueprint reflects your latest paid assessment.";
  const refreshStatusCopy = planAccess.requires_new_purchase_for_refresh
    ? "You have already used your included Blueprint refresh. Updating your assessment again will require a new paid Blueprint if you want refreshed results and guidance."
    : planAccess.included_refresh_available
      ? "Your paid Blueprint is based on your current assessment and includes one free refresh if you update your answers later."
      : "Your included refresh has already been used, but this Blueprint remains available any time you want to revisit it.";
  const relocationBudgetKey = planSummary?.budget_abroad || planSummary?.budget || "";
  const relocationBudgetDetails = getRelocationBudgetDetails(relocationBudgetKey);
  const budgetRealityCheckRows = buildBudgetFitRows(shortlistedCountries, relocationBudgetKey, planSummary.family_status);
  const budgetRealityTopRow = budgetRealityCheckRows[0] || null;
  const budgetRealityPressureRow = budgetRealityCheckRows[budgetRealityCheckRows.length - 1] || null;
  const budgetRealitySupportNotes = [
    budgetRealityTopRow
      ? {
          title: "Where the budget goes furthest",
          text: buildBudgetSupportNote(budgetRealityTopRow, "strength"),
        }
      : null,
    budgetRealityPressureRow && budgetRealityPressureRow.code !== budgetRealityTopRow?.code
      ? {
          title: "Where the budget tightens fastest",
          text: buildBudgetSupportNote(budgetRealityPressureRow, "pressure"),
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="rc-page-shell min-h-screen bg-background flex flex-col" data-testid="relocation-plan-page">
      <Header />

      <main className="rc-container py-8 md:py-12">
        <div className="rc-panel mb-10 p-8 sm:p-10 opacity-0 animate-fade-in-up">
          <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="relocation-plan-header-accent" />
          <p className="rc-section-kicker mb-3">Personalized Blueprint</p>
          <h1 className="font-manrope text-4xl md:text-5xl text-stone-900 mb-3" data-testid="relocation-plan-title">
            Your Relocation Blueprint
          </h1>
          <p className="max-w-3xl text-sm leading-7 text-stone-600" data-testid="relocation-plan-subtitle">
            This Blueprint brings together your strongest options, the tradeoffs that matter, and the next steps toward choosing a country with confidence.
          </p>
          {planPriorityFocusLine ? (
            <p className="mt-3 max-w-3xl text-sm leading-7 text-stone-600" data-testid="plan-priority-focus-line">
              {tightenPlanPrecisionCopy(planPriorityFocusLine)}
            </p>
          ) : null}
          {showDebugPanel ? (
            <div className="mt-5 rounded-[1.4rem] border border-amber-200 bg-amber-50/80 px-5 py-4 text-sm text-stone-700" data-testid="plan-debug-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700" data-testid="plan-debug-heading">Preview debug</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="plan-debug-grid">
                <p data-testid="plan-debug-email"><span className="font-medium text-stone-900">User email:</span> {user?.email || "Unavailable"}</p>
                <p data-testid="plan-debug-plan-id"><span className="font-medium text-stone-900">Plan ID:</span> {plan?.id || planAccess.current_plan_id || "None"}</p>
                <p data-testid="plan-debug-generated-at"><span className="font-medium text-stone-900">Plan generated:</span> {plan?.created_at || planAccess.current_plan_created_at || "None"}</p>
                <p data-testid="plan-debug-plan-intake-id"><span className="font-medium text-stone-900">Plan intake ID:</span> {planAccess.current_plan_based_on_intake_id || "None"}</p>
              </div>
            </div>
          ) : null}
          <div className="mt-6 rounded-[1.5rem] border border-stone-200 bg-stone-50/80 p-5" data-testid="plan-refresh-status-panel">
            <p className="text-sm font-semibold text-stone-900" data-testid="plan-refresh-status-title">{refreshStatusTitle}</p>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-stone-600" data-testid="plan-refresh-status-copy">
              {refreshStatusCopy}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button asChild variant="outline" className="rounded-full border-stone-300 px-4 py-2 text-stone-700 hover:border-orange-300 hover:bg-orange-50/70 hover:text-stone-900" data-testid="plan-update-assessment-link">
                <Link to="/assessment?step=review">Update Assessment</Link>
              </Button>
              {planAccess.requires_new_purchase_for_refresh ? (
                <Button onClick={handleUpgrade} className="rounded-full bg-stone-900 px-5 py-2 text-white hover:bg-orange-600" data-testid="plan-refresh-purchase-button">
                  Purchase Refreshed Plan
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        <section className="mb-10" data-testid="relocation-snapshot-section">
          <div className="max-w-4xl mb-4">
            <h2 className="font-manrope text-3xl text-stone-900 mb-3">Your leading options</h2>
            <p className="text-sm leading-7 text-stone-600" data-testid="relocation-snapshot-subtitle">
              This section highlights your strongest option, your best backup option, and the tradeoff most likely to shape your final decision.
            </p>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.15fr_1fr_1fr]" data-testid="relocation-snapshot-box">
            <Card className="rounded-[1.8rem] border-orange-200/70 bg-white shadow-[0_18px_40px_rgba(28,25,23,0.07)]" data-testid="plan-leading-country-card">
              <CardContent className="p-6 space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-700" data-testid="plan-leading-country-kicker">
                    Strongest option right now
                  </p>
                  <span className="inline-flex rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700" data-testid="plan-leading-country-badge">
                    Best fit today
                  </span>
                </div>
                {leadingCountrySnapshot?.name ? (
                  <CountryLabel
                    code={leadingCountrySnapshot.code}
                    name={leadingCountrySnapshot.name}
                    size={22}
                    dataTestId="plan-leading-country-label"
                    flagTestId="plan-leading-country-flag"
                    textClassName="font-manrope text-2xl text-stone-900"
                  />
                ) : (
                  <p className="font-manrope text-2xl text-stone-900" data-testid="plan-leading-country-fallback-name">Lead option pending</p>
                )}
                <p className="text-sm leading-7 text-stone-700" data-testid="plan-leading-country-summary">
                  {tightenPlanPrecisionCopy(leadingCountrySnapshot?.summary || snapshotFallback.overview)}
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-[1.8rem] border-stone-200/80 bg-white/90 shadow-[0_18px_40px_rgba(28,25,23,0.06)]" data-testid="plan-strongest-alternative-card">
              <CardContent className="p-6 space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500" data-testid="plan-strongest-alternative-kicker">
                  Strongest backup option
                </p>
                {strongestAlternativeSnapshot?.name ? (
                  <CountryLabel
                    code={strongestAlternativeSnapshot.code}
                    name={strongestAlternativeSnapshot.name}
                    size={20}
                    dataTestId="plan-strongest-alternative-label"
                    flagTestId="plan-strongest-alternative-flag"
                    textClassName="font-manrope text-xl text-stone-900"
                  />
                ) : (
                  <p className="font-manrope text-xl text-stone-900" data-testid="plan-strongest-alternative-fallback-name">Alternative pending</p>
                )}
                <p className="text-sm leading-7 text-stone-700" data-testid="plan-strongest-alternative-summary">
                  {tightenPlanPrecisionCopy(strongestAlternativeSnapshot?.summary || planConstraint)}
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-[1.8rem] border-stone-200/80 bg-stone-50/90 shadow-[0_18px_40px_rgba(28,25,23,0.05)]" data-testid="plan-main-decision-tension-card">
              <CardContent className="p-6 space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500" data-testid="plan-main-decision-tension-kicker">
                  Main deciding tradeoff
                </p>
                {leadingCountrySnapshot?.name && strongestAlternativeSnapshot?.name ? (
                  <p className="font-manrope text-xl text-stone-900" data-testid="plan-main-decision-tension-pair">
                    {leadingCountrySnapshot.name} vs {strongestAlternativeSnapshot.name}
                  </p>
                ) : null}
                <p className="text-xs leading-6 text-stone-500" data-testid="plan-main-decision-tension-helper">
                  This is the tradeoff most likely to change the answer.
                </p>
                <p className="text-sm leading-7 text-stone-700" data-testid="plan-main-decision-tension-summary">
                  {tightenPlanPrecisionCopy(mainDecisionTension)}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-4 rounded-[1.7rem] border border-stone-200/80 bg-stone-50/90 p-5 shadow-[0_18px_40px_rgba(28,25,23,0.05)]" data-testid="plan-narrowing-guidance-panel">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2" data-testid="plan-constraint-title">What needs a closer look</p>
                <p className="text-sm leading-7 text-stone-700" data-testid="plan-constraint-value">{tightenPlanPrecisionCopy(planConstraint)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2" data-testid="plan-narrowing-insight-title">What is most likely to clarify the choice</p>
                <p className="text-sm leading-7 text-stone-700" data-testid="plan-narrowing-insight-value">{tightenPlanPrecisionCopy(planNarrowingInsight)}</p>
              </div>
            </div>
            {plan?.healthcare_decision_contrast ? (
              <p className="mt-4 border-t border-stone-200/80 pt-4 text-sm leading-7 text-stone-700" data-testid="plan-healthcare-decision-contrast">
                {plan.healthcare_decision_contrast}
              </p>
            ) : null}
            {planRemoteDecisionContrast ? (
              <p className="mt-4 text-sm leading-7 text-stone-700" data-testid="plan-remote-decision-contrast">
                {planRemoteDecisionContrast}
              </p>
            ) : null}
          </div>
        </section>

        {/* How Priorities Shaped This Plan */}
        <section className="mb-10" data-testid="priorities-section">
          <h2 className="font-manrope text-3xl text-stone-900 mb-3">What matters most</h2>
          <p className="text-sm leading-7 text-stone-600 max-w-3xl mb-3" data-testid="priorities-helper-line">
            These priorities are doing most of the shortlist and ranking work. The other signals below help explain the kind of move you are trying to make and how closer calls are being separated.
          </p>
          {topPriorityLabels.length ? (
            <div className="mb-3" data-testid="plan-top-priorities-block">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Your top priorities</p>
              <div className="flex flex-wrap gap-2" data-testid="plan-top-priorities-chips">
                {topPriorityLabels.map((priority) => (
                  <div
                    key={priority}
                    className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1.5 text-xs font-medium text-stone-900"
                    data-testid={`plan-top-priority-${priority.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {priority}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {(visibleMotivationLabels.length || visibleLifestyleRefinerLabels.length) ? (
            <div className="mt-4 grid gap-3 md:grid-cols-2" data-testid="plan-priority-secondary-signals-grid">
              {visibleMotivationLabels.length ? (
                <div className="rounded-[1.25rem] border border-stone-200/80 bg-stone-50/80 p-4" data-testid="plan-priority-motivation-context">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Why this move is happening now</p>
                  <p className="mt-2 text-sm leading-7 text-stone-700">
                    {`Your answers around ${formatThemeList(visibleMotivationLabels)} help explain the kind of move you are trying to make and why some tradeoffs receive more emphasis.`}
                  </p>
                </div>
              ) : null}
              {visibleLifestyleRefinerLabels.length ? (
                <div className="rounded-[1.25rem] border border-stone-200/80 bg-stone-50/80 p-4" data-testid="plan-priority-lifestyle-refiner-context">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">How close calls are being separated</p>
                  <p className="mt-2 text-sm leading-7 text-stone-700">
                    {`Lifestyle refiners like ${formatThemeList(visibleLifestyleRefinerLabels)} help break ties once the bigger practical factors are already close, and they shape the day-to-day guidance surfaced later in the Blueprint.`}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>

        {/* Profile Snapshot */}
        <section className="mb-10" data-testid="profile-snapshot-section">
          <h2 className="font-manrope text-3xl text-stone-900 mb-3">Your Blueprint Is Based On</h2>
          <p className="text-sm leading-7 text-stone-600 max-w-3xl mb-3" data-testid="profile-snapshot-helper-line">
            These are the details you provided that personalized your Blueprint recommendations.
          </p>
          {(visibleMotivationLabels.length || visibleLifestyleRefinerLabels.length) ? (
            <div className="mb-4 rounded-[1.5rem] border border-stone-200/80 bg-stone-50/80 p-4" data-testid="profile-snapshot-recognized-inputs-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500" data-testid="profile-snapshot-recognized-inputs-heading">
                Also reflected in the Blueprint
              </p>
              {visibleMotivationLabels.length ? (
                <div className="mt-3" data-testid="profile-snapshot-motivation-block">
                  <p className="text-sm font-medium text-stone-900">What is pulling you toward the move</p>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="profile-snapshot-motivation-chips">
                    {visibleMotivationLabels.map((label, index) => (
                      <span
                        key={`plan-motivation-${label}`}
                        className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-700"
                        data-testid={`profile-snapshot-motivation-chip-${index + 1}`}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
              {visibleLifestyleRefinerLabels.length ? (
                <div className="mt-4" data-testid="profile-snapshot-lifestyle-refiner-block">
                  <p className="text-sm font-medium text-stone-900">Lifestyle refiners helping break close calls</p>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="profile-snapshot-lifestyle-refiner-chips">
                    {visibleLifestyleRefinerLabels.map((label, index) => (
                      <span
                        key={`plan-lifestyle-refiner-${label}`}
                        className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-700"
                        data-testid={`profile-snapshot-lifestyle-refiner-chip-${index + 1}`}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                  <p className="mt-2 text-xs leading-6 text-stone-500" data-testid="profile-snapshot-lifestyle-refiner-helper-text">
                    These refiners do not outweigh your main priorities. They help separate close options and shape the day-to-day reasoning surfaced throughout the Blueprint.
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="rounded-[1.7rem] border border-stone-200/80 bg-white/88 p-5 shadow-[0_16px_36px_rgba(28,25,23,0.05)]">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="profile-snapshot-visible-grid">
              {profileItems.map((item) => (
                <div
                  key={item.label}
                  className="rounded-[1.2rem] border border-stone-200 bg-stone-50/90 px-4 py-3"
                  data-testid={`profile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1">{item.label}</p>
                  <p className="text-sm font-medium text-stone-900">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-12" data-testid="plan-budget-reality-check-section">
          <h2 className="font-manrope text-3xl text-stone-900 mb-3">How far does your budget go?</h2>
          <p className="text-sm leading-7 text-stone-600 max-w-3xl mb-4" data-testid="plan-budget-reality-check-helper-line">
            Use this table to see how your stated relocation budget holds up once monthly costs are compared across the shortlist.
          </p>
          <div className="rounded-[1.8rem] border border-stone-200/80 bg-white/92 p-5 shadow-[0_16px_36px_rgba(28,25,23,0.05)]" data-testid="plan-budget-reality-check-table-wrapper">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3" data-testid="plan-budget-reality-check-selected-budget">
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Selected relocation budget</p>
                <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800" data-testid="plan-budget-reality-check-selected-budget-label">
                  {relocationBudgetDetails?.label || "Budget range not specified"}
                </span>
              </div>
              <p className="text-xs text-stone-500" data-testid="plan-budget-reality-check-usd-note">
                USD estimates are approximate.
              </p>
            </div>

            <div className="hidden overflow-x-auto md:block" data-testid="plan-budget-reality-check-desktop-table">
              <table className="w-full min-w-[640px] border-separate border-spacing-0 text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                    <th className="border-b border-stone-200 px-4 pb-3" data-testid="budget-fit-header-country">Country</th>
                    <th className="border-b border-stone-200 px-4 pb-3" data-testid="budget-fit-header-range">Typical monthly range</th>
                    <th className="border-b border-stone-200 px-4 pb-3" data-testid="budget-fit-header-label">Budget fit</th>
                    <th className="border-b border-stone-200 px-4 pb-3" data-testid="budget-fit-header-commentary">Commentary</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetRealityCheckRows.map((row, index) => (
                    <tr key={row.code} className={index % 2 === 0 ? "bg-stone-50/70" : "bg-white"} data-testid={`plan-budget-reality-check-row-${row.code}`}>
                      <td className="border-b border-stone-100 px-4 py-4 align-top">
                        <CountryLabel
                          code={row.code}
                          name={row.name}
                          size={16}
                          className="items-start gap-2"
                          flagClassName="mt-0.5"
                          textClassName="font-medium text-stone-900"
                          dataTestId={`plan-budget-reality-check-country-${row.code}`}
                          flagTestId={`plan-budget-reality-check-country-flag-${row.code}`}
                        />
                      </td>
                      <td className="border-b border-stone-100 px-4 py-4 align-top text-stone-700" data-testid={`plan-budget-reality-check-range-${row.code}`}>
                        <div>
                          <p className="text-sm font-medium text-stone-700">{row.range}</p>
                          {row.usdRange ? (
                            <p className="mt-1 text-xs text-stone-500" data-testid={`plan-budget-reality-check-usd-range-${row.code}`}>
                              {row.usdRange}
                            </p>
                          ) : null}
                        </div>
                      </td>
                      <td className="border-b border-stone-100 px-4 py-4 align-top">
                        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${BUDGET_FIT_TONE_STYLES[row.fitLabel] || "border-stone-200 bg-stone-50 text-stone-800"}`} data-testid={`plan-budget-reality-check-fit-${row.code}`}>
                          {row.fitLabel}
                        </span>
                      </td>
                      <td className="border-b border-stone-100 px-4 py-4 align-top text-stone-600" data-testid={`plan-budget-reality-check-commentary-${row.code}`}>
                        {row.commentary}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 md:hidden" data-testid="plan-budget-reality-check-mobile-cards">
              {budgetRealityCheckRows.map((row) => (
                <div key={row.code} className="rounded-[1.2rem] border border-stone-200 bg-stone-50/70 p-4" data-testid={`plan-budget-reality-check-mobile-card-${row.code}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <CountryLabel
                      code={row.code}
                      name={row.name}
                      size={16}
                      className="items-start gap-2"
                      flagClassName="mt-0.5"
                      textClassName="font-medium text-stone-900"
                      dataTestId={`plan-budget-reality-check-mobile-country-${row.code}`}
                      flagTestId={`plan-budget-reality-check-mobile-country-flag-${row.code}`}
                    />
                    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${BUDGET_FIT_TONE_STYLES[row.fitLabel] || "border-stone-200 bg-white text-stone-800"}`} data-testid={`plan-budget-reality-check-mobile-fit-${row.code}`}>
                      {row.fitLabel}
                    </span>
                  </div>
                  <div className="mt-3" data-testid={`plan-budget-reality-check-mobile-range-${row.code}`}>
                    <p className="text-sm font-medium text-stone-700">{row.range}</p>
                    {row.usdRange ? (
                      <p className="mt-1 text-xs text-stone-500" data-testid={`plan-budget-reality-check-mobile-usd-range-${row.code}`}>
                        {row.usdRange}
                      </p>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm leading-7 text-stone-600" data-testid={`plan-budget-reality-check-mobile-commentary-${row.code}`}>
                    {row.commentary}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {budgetRealitySupportNotes.length ? (
            <div className="mt-4 grid gap-3 md:grid-cols-2" data-testid="plan-budget-reality-check-support-notes">
              {budgetRealitySupportNotes.map((note, index) => (
                <div key={note.title} className="rounded-[1.25rem] border border-stone-200/80 bg-stone-50/70 px-4 py-4" data-testid={`plan-budget-reality-check-support-note-${index + 1}`}>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">{note.title}</p>
                  <p className="mt-2 text-sm leading-7 text-stone-700">{note.text}</p>
                </div>
              ))}
            </div>
          ) : null}
        </section>

        {/* Countries in Your Relocation Blueprint */}
        <section className="mb-12" data-testid="countries-section">
          <h2 className="font-manrope text-3xl text-stone-900 mb-4">Why is each country still in the mix?</h2>
          <p className="text-sm leading-7 text-stone-600 max-w-3xl mb-4" data-testid="countries-section-helper-line">
            Each country remains here for a different reason. Start with the overview at the top of each country card, then use the sections below to see why it remains a strong option in your Blueprint.
          </p>
          <div className="grid lg:grid-cols-2 gap-6">
            {shortlistedCountries.map((country, index) => {
              const sectionReasoning = planSectionReasoningMap[country.code] || {};
              const strategicBullets = sectionReasoning.strategicBullets?.length
                ? sectionReasoning.strategicBullets
                : [sectionReasoning.verificationBullets?.[0], sectionReasoning.tradeoffs?.[0]].filter(Boolean).slice(0, 2);
              const countrySlug = getCountryPreviewSlugByCode(country.code) || country.name.toLowerCase().replace(/\s+/g, "-");
              const fallbackCountryRole = getCountryRoleMeta(country, index, shortlistedCountries);
              const alignedHealthcare = getAlignedHealthcareCardContent(country);
              const regionGuidance = getCountryRegionGuidance(country, planSummary);
              const budgetRealityContext = getBudgetRealityContext(country, regionGuidance, planSummary);
              const residencyComparison = getResidencyComparisonNote(country);
              const residencyPathwayDetail = getResidencyPathwayDetail(country, residencyComparison);
              const expatFamiliaritySignal = getExpatFamiliaritySignal(country, planSummary);
              const healthcareLabel = alignedHealthcare.label || getHealthcareDisplayLabel(country);
              const healthcareRead = alignedHealthcare.summary;
              const healthcareConstraintNote = alignedHealthcare.constraint || "";
              const specificRoleSummary = getSpecificPlanRoleSummary(country, index, shortlistedCountries);
              const countryRole = {
                label: tightenPlanPrecisionCopy(country.role_label || fallbackCountryRole.label),
                summary: tightenPlanPrecisionCopy(tightenPlanCardCopy(specificRoleSummary || country.plan_role_summary || country.role_summary || fallbackCountryRole.summary, country)),
              };
              const tightenedStrategicBullets = sanitizeRenderableList(
                strategicBullets.map((reason) => tightenPlanPrecisionCopy(tightenPlanCardCopy(reason, country))),
                2
              );
              const tightenedHealthcareRead = tightenPlanPrecisionCopy(tightenPlanCardCopy(healthcareRead, country));
              const tightenedHealthcareConstraintNote = tightenPlanPrecisionCopy(tightenPlanCardCopy(healthcareConstraintNote, country));
              const hasHealthcareSection = hasMeaningfulText(tightenedHealthcareRead) || hasMeaningfulText(tightenedHealthcareConstraintNote);
              const hasStrategicSection = tightenedStrategicBullets.length > 0;
              const hasRegionGuidance = regionGuidance?.places?.length;
              const hasResidencyPathwaySection = hasMeaningfulText(residencyPathwayDetail.primary) || hasMeaningfulText(residencyPathwayDetail.routeShape) || hasMeaningfulText(residencyPathwayDetail.watchout);
              return (
                <Card key={country.code} className="overflow-hidden rounded-[1.9rem] border-stone-200/80 bg-white/92 shadow-[0_18px_40px_rgba(28,25,23,0.06)]" data-testid={`plan-country-card-${country.code}`}>
                  <CardContent className="p-6 space-y-4">
                    <div>
                      <CountryLabel
                        code={country.code}
                        name={country.name}
                        size={18}
                        className="items-start gap-2"
                        flagClassName="mt-0.5"
                        textClassName="font-manrope text-lg font-semibold text-foreground"
                        dataTestId={`plan-country-${country.code}`}
                        flagTestId={`plan-country-flag-${country.code}`}
                      />
                      <div>
                        <p className="mt-1 text-xs text-stone-500" data-testid={`plan-country-region-${country.code}`}>{country.region}</p>
                        <p
                          className="mt-1 max-w-[260px] truncate text-xs text-stone-500"
                          title={reviewedLabel}
                          data-testid={`plan-country-review-signal-${country.code}`}
                        >
                          {reviewedLabel}
                        </p>
                        <CountrySafetySignal
                          safetyProfile={country.safety_profile}
                          showContext
                          dataTestIdPrefix={`plan-country-safety-signal-${country.code}`}
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-700" data-testid={`plan-country-role-label-${country.code}`}>
                          {countryRole.label}
                        </span>
                        {renderIndicator(getPriorityAlignment(country.score), `plan-country-alignment-${country.code}`)}
                      </div>
                      <p className="text-sm leading-7 text-stone-700" data-testid={`plan-country-role-summary-${country.code}`}>
                        {countryRole.summary}
                      </p>
                      {planPaceSignalState.showCountryPaceDescriptions ? (
                        <p className="text-xs leading-6 text-stone-500" data-testid={`plan-country-pace-description-${country.code}`}>
                          {`Daily pace: ${getCountryDailyPaceDescription(country.code)}`}
                        </p>
                      ) : null}
                      {isRemoteRelevant(planSummary) ? (
                        <p className="text-xs leading-6 text-stone-500" data-testid={`plan-country-remote-compatibility-${country.code}`}>
                          {`Remote work: ${getCountryRemoteCompatibilityLabel(country, planSummary)}`}
                        </p>
                      ) : null}
                    </div>
                    <div className={`grid gap-3 ${expatFamiliaritySignal ? "sm:grid-cols-2 xl:grid-cols-4" : "sm:grid-cols-3"}`} data-testid={`plan-country-signal-grid-${country.code}`}>
                      <div className="rounded-[1.2rem] border border-stone-200/80 bg-stone-50/80 p-4" data-testid={`plan-country-budget-signal-${country.code}`}>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">Budget context</p>
                        <div className="mt-2">{renderIndicator(getFinancialIndicator(country.cost_index), `plan-country-budget-indicator-${country.code}`)}</div>
                      </div>
                      <div className="rounded-[1.2rem] border border-stone-200/80 bg-stone-50/80 p-4" data-testid={`plan-country-healthcare-signal-${country.code}`}>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">Healthcare read</p>
                        <div className="mt-2">{renderIndicator(getHealthcareIndicator(country.healthcare_rating), `plan-country-healthcare-indicator-${country.code}`)}</div>
                      </div>
                      <div className="rounded-[1.2rem] border border-stone-200/80 bg-stone-50/80 p-4" data-testid={`plan-country-residency-signal-${country.code}`}>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">Residency pathway</p>
                        <div className="mt-2">{renderIndicator(getResidencyIndicator(country.visa_difficulty), `plan-country-residency-indicator-${country.code}`)}</div>
                      </div>
                      {expatFamiliaritySignal ? (
                        <div className="rounded-[1.2rem] border border-stone-200/80 bg-stone-50/80 p-4" data-testid={`plan-country-expat-familiarity-signal-${country.code}`}>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">Expat familiarity</p>
                          <p className="mt-2 text-sm font-medium leading-6 text-stone-900" data-testid={`plan-country-expat-familiarity-label-${country.code}`}>
                            {expatFamiliaritySignal.label}
                          </p>
                        </div>
                      ) : null}
                    </div>
                    {expatFamiliaritySignal ? (
                      <div className="rounded-[1.4rem] border border-stone-200/80 bg-stone-50/80 p-4" data-testid={`plan-country-expat-familiarity-${country.code}`}>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500" data-testid={`plan-country-expat-familiarity-heading-${country.code}`}>
                          Expat familiarity
                        </p>
                        <p className="mt-2 text-sm leading-7 text-stone-700" data-testid={`plan-country-expat-familiarity-copy-${country.code}`}>
                          {expatFamiliaritySignal.copy}
                        </p>
                      </div>
                    ) : null}
                    {hasRegionGuidance ? (
                      <div className="rounded-[1.4rem] border border-stone-200/80 bg-stone-50/80 p-4" data-testid={`plan-country-region-guidance-${country.code}`}>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500" data-testid={`plan-country-region-guidance-heading-${country.code}`}>
                          Where to start looking first
                        </p>
                        <TooltipProvider>
                          <div className="mt-3 flex flex-wrap gap-2" data-testid={`plan-country-region-guidance-places-${country.code}`}>
                            {regionGuidance.places.map((place, placeIndex) => (
                              <Tooltip key={`${country.code}-${place}`}>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="rounded-full border border-stone-200 bg-white px-3 py-1 text-xs font-medium text-stone-800 transition-[border-color,background-color,color] duration-200 hover:border-orange-300 hover:bg-orange-50/80 hover:text-stone-900"
                                    data-testid={`plan-country-region-guidance-place-${country.code}-${place.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                  >
                                    {place}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[220px] text-sm leading-6" data-testid={`plan-country-region-guidance-tooltip-${country.code}-${place.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                  {getRegionPlaceContext(place, placeIndex)}
                                </TooltipContent>
                              </Tooltip>
                            ))}
                          </div>
                        </TooltipProvider>
                        {hasMeaningfulText(regionGuidance.rationale) ? (
                          <p className="mt-3 text-sm leading-7 text-stone-700" data-testid={`plan-country-region-guidance-copy-${country.code}`}>
                            {regionGuidance.rationale}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {hasHealthcareSection ? (
                      <div className="rounded-[1.4rem] border border-stone-200/80 bg-stone-50/80 p-4" data-testid={`plan-country-healthcare-block-${country.code}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500" data-testid={`plan-country-healthcare-heading-${country.code}`}>Healthcare read</p>
                          <span className="rounded-full border border-stone-200 bg-white px-3 py-1 text-[11px] font-medium text-stone-900" data-testid={`plan-country-healthcare-label-${country.code}`}>
                            {healthcareLabel}
                          </span>
                        </div>
                        {hasMeaningfulText(tightenedHealthcareRead) ? (
                          <p className="mt-2 text-sm leading-7 text-stone-700" data-testid={`plan-country-healthcare-summary-${country.code}`}>
                            {tightenedHealthcareRead}
                          </p>
                        ) : null}
                        {hasMeaningfulText(tightenedHealthcareConstraintNote) ? (
                          <p className="mt-2 text-xs leading-6 text-stone-500" data-testid={`plan-country-healthcare-constraint-${country.code}`}>
                            {tightenedHealthcareConstraintNote}
                          </p>
                        ) : null}
                        {hasMeaningfulText(regionGuidance?.healthcareNote) ? (
                          <p className="mt-2 text-xs leading-6 text-stone-500" data-testid={`plan-country-healthcare-location-note-${country.code}`}>
                            {regionGuidance.healthcareNote}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {budgetRealityContext ? (
                      <div className="rounded-[1.4rem] border border-stone-200/80 bg-white/90 p-4" data-testid={`plan-country-budget-context-${country.code}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500" data-testid={`plan-country-budget-context-heading-${country.code}`}>What the budget looks like here</p>
                          <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-[11px] font-medium text-stone-900" data-testid={`plan-country-budget-context-label-${country.code}`}>
                            {budgetRealityContext.label}
                          </span>
                        </div>
                        <p className="mt-2 text-sm leading-7 text-stone-700" data-testid={`plan-country-budget-context-copy-${country.code}`}>
                          {budgetRealityContext.copy}
                        </p>
                      </div>
                    ) : null}
                    {hasResidencyPathwaySection ? (
                      <div className="rounded-[1.4rem] border border-stone-200/80 bg-white/90 p-4" data-testid={`plan-country-residency-pathway-${country.code}`}>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500" data-testid={`plan-country-residency-pathway-heading-${country.code}`}>
                          Which residency pathway is most realistic?
                        </p>
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                          <div className="rounded-[1rem] border border-stone-200 bg-stone-50/80 p-3" data-testid={`plan-country-primary-pathway-${country.code}`}>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">Most likely residency pathway</p>
                            <p className="mt-2 text-sm leading-6 text-stone-800">{residencyPathwayDetail.primary}</p>
                          </div>
                          {hasMeaningfulText(residencyPathwayDetail.alternative) ? (
                            <div className="rounded-[1rem] border border-stone-200 bg-stone-50/80 p-3" data-testid={`plan-country-alternative-pathway-${country.code}`}>
                              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">Also worth exploring</p>
                              <p className="mt-2 text-sm leading-6 text-stone-800">{residencyPathwayDetail.alternative}</p>
                            </div>
                          ) : null}
                        </div>
                        {hasMeaningfulText(residencyPathwayDetail.routeShape) ? (
                          <p className="mt-3 text-sm leading-7 text-stone-700" data-testid={`plan-country-route-shape-${country.code}`}>
                            {residencyPathwayDetail.routeShape}
                          </p>
                        ) : null}
                        {hasMeaningfulText(residencyPathwayDetail.watchout) ? (
                          <p className="mt-2 text-xs leading-6 text-stone-500" data-testid={`plan-country-residency-watchout-${country.code}`}>
                            {residencyPathwayDetail.watchout}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {hasStrategicSection ? (
                      <div className="rounded-[1.4rem] border border-stone-200/80 bg-stone-50/60 p-4" data-testid={`plan-country-strategic-section-${country.code}`}>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500 mb-2" data-testid={`plan-country-strategic-heading-${country.code}`}>
                          Why it is still worth considering
                        </p>
                        <ul className="space-y-2 text-sm leading-7 text-stone-600" data-testid={`plan-country-strategic-bullets-${country.code}`}>
                          {tightenedStrategicBullets.map((reason, idx) => (
                            <li key={idx} className="flex items-start gap-2" data-testid={`plan-country-strategic-bullet-${country.code}-${idx + 1}`}>
                              <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-600" />
                              <span>{reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    <div className="pt-2" data-testid={`country-details-cta-block-${country.code}`}>
                      <p className="text-sm leading-7 text-stone-600" data-testid={`country-details-cta-copy-${country.code}`}>
                        Go beyond the Blueprint to see how daily life, regional differences, residency considerations, and practical setup could shape your decision.
                      </p>
                      <Link
                        to={`/country/${countrySlug}?entry=plan`}
                        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-amber-700 transition-[color,transform] duration-200 hover:text-amber-800 hover:underline"
                        data-testid={`country-details-link-${country.code}`}
                      >
                        {`See what it's like to live in ${country.name} →`}
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Residency Pathways Overview */}
        <section className="mb-12" data-testid="residency-overview-section">
          <h2 className="font-manrope text-3xl text-stone-900 mb-4">How realistic is the residency pathway?</h2>
          <p className="text-sm leading-7 text-stone-600 max-w-3xl mb-4" data-testid="residency-overview-helper-line">
            Use this section to compare how practical each residency pathway looks, which pathway best fits your situation, and what could slow the process.
          </p>
          <div className="space-y-4">
            {shortlistedCountries.map((country) => {
              const residencyComparison = getResidencyComparisonNote(country);
              const residencySections = [
                {
                  key: "practicality",
                  title: "How practical is this residency pathway?",
                  copy: residencyComparison.practicality,
                  testId: `residency-practicality-block-${country.code}`,
                  copyTestId: `residency-practicality-copy-${country.code}`,
                },
                {
                  key: "route-shape",
                  title: "Which residency pathway fits best?",
                  copy: residencyComparison.routeShape,
                  testId: `residency-route-shape-block-${country.code}`,
                  copyTestId: `residency-route-shape-copy-${country.code}`,
                },
                {
                  key: "watchout",
                  title: "What could slow the process?",
                  copy: residencyComparison.watchout,
                  testId: `residency-watchout-block-${country.code}`,
                  copyTestId: `residency-watchout-copy-${country.code}`,
                },
              ].filter((section) => hasMeaningfulText(section.copy));
              const residencyGridClassName = residencySections.length >= 3
                ? "md:grid-cols-3"
                : residencySections.length === 2
                  ? "md:grid-cols-2"
                  : "";

              if (!residencySections.length) {
                return null;
              }

              return (
                <div key={`${country.code}-residency`} className="rounded-[1.8rem] border border-stone-200/80 bg-white/90 p-5 shadow-[0_16px_36px_rgba(28,25,23,0.05)]" data-testid={`residency-card-${country.code}`}>
                  <div className="flex items-start gap-3 flex-wrap mb-4">
                    <h3 className="font-manrope text-2xl text-stone-900" data-testid={`residency-card-title-${country.code}`}>
                      <CountryLabel
                        code={country.code}
                        name={country.name}
                        size={18}
                        flagTestId={`residency-card-flag-${country.code}`}
                      />
                    </h3>
                  </div>
                  <div className={`grid gap-3 ${residencyGridClassName}`} data-testid={`residency-card-grid-${country.code}`}>
                    {residencySections.map((section) => (
                      <div key={`${country.code}-${section.key}`} className="rounded-[1.25rem] border border-stone-200 bg-stone-50/80 p-4" data-testid={section.testId}>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">{section.title}</p>
                        <p className="mt-2 text-sm leading-7 text-stone-700" data-testid={section.copyTestId}>{section.copy}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-stone-500 mt-4">
            Requirements change over time — always verify current rules with official sources or qualified professionals.
          </p>
        </section>

        {/* Key Tradeoffs to Consider */}
        <section className="mb-12" data-testid="key-tradeoffs-section">
          <h2 className="font-manrope text-3xl text-stone-900 mb-4">What could weaken each option?</h2>
          <p className="text-sm leading-7 text-stone-600 max-w-3xl mb-4" data-testid="key-tradeoffs-helper-line">
            Use this section for the friction points that could weaken each country once the move gets more specific.
          </p>
          <div className="space-y-6">
            {shortlistedCountries.map((country) => {
              const sectionReasoning = planSectionReasoningMap[country.code] || {};
              const strategicBullets = Array.isArray(sectionReasoning.strategicBullets)
                ? sanitizeRenderableList(sectionReasoning.strategicBullets, 2)
                : [];
              const tradeoffs = Array.isArray(sectionReasoning.tradeoffs)
                ? sanitizeRenderableList(sectionReasoning.tradeoffs, 3)
                : sanitizeRenderableList(Array.from(new Set(getTradeoffs(country))), 3);
              const verificationBullets = Array.isArray(sectionReasoning.verificationBullets)
                ? sanitizeRenderableList(sectionReasoning.verificationBullets, 2)
                : sanitizeRenderableList(buildPlanVerificationBullets(country, tradeoffs), 2);
              if (!tradeoffs.length && !verificationBullets.length && !strategicBullets.length) {
                return null;
              }
              const hasTradeoffSection = tradeoffs.length > 0;
              const hasVerificationSection = verificationBullets.length > 0;
              const shouldSurfaceStrategicInterpretation = strategicBullets.length > 0;
              const visibleSections = [shouldSurfaceStrategicInterpretation, hasTradeoffSection, hasVerificationSection].filter(Boolean).length;
              const detailGridClassName = visibleSections >= 3
                ? "grid w-full gap-6 xl:grid-cols-3"
                : hasTradeoffSection && hasVerificationSection
                  ? "grid w-full gap-6 md:grid-cols-2"
                  : "grid w-full grid-cols-1 gap-6 md:grid-cols-1";
              const detailSectionClassName = "w-full min-w-0 max-w-none";

              return (
                <div
                  key={country.code}
                  className="rounded-[1.9rem] border border-stone-200/80 bg-white/90 p-6 shadow-[0_18px_40px_rgba(28,25,23,0.05)]"
                  data-testid={`tradeoff-country-${country.code}`}
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <h3 className="font-manrope text-2xl text-stone-900" data-testid={`tradeoff-country-title-${country.code}`}>
                      <CountryLabel
                        code={country.code}
                        name={country.name}
                        size={18}
                        flagTestId={`tradeoff-country-flag-${country.code}`}
                      />
                    </h3>
                  </div>
                  {shouldSurfaceStrategicInterpretation ? (
                    <div className="mb-5 rounded-[1.35rem] border border-stone-200/80 bg-stone-50/70 p-4" data-testid={`tradeoff-country-meaning-section-${country.code}`}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2" data-testid={`tradeoff-country-meaning-heading-${country.code}`}>What this means for your decision</p>
                      <ul className="list-disc list-inside space-y-2 text-sm leading-7 text-stone-600">
                        {strategicBullets.map((item, index) => (
                          <li key={`${country.code}-meaning-${index}`} data-testid={`tradeoff-country-meaning-item-${country.code}-${index + 1}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <div className={detailGridClassName} data-testid={`tradeoff-country-expanded-${country.code}`}>
                      {hasTradeoffSection ? (
                        <div className={detailSectionClassName} data-testid={`tradeoff-country-risk-section-${country.code}`}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2" data-testid={`tradeoff-country-risk-heading-${country.code}`}>What could weaken the case</p>
                          <ul className="list-disc list-inside space-y-2 text-sm leading-7 text-stone-600">
                            {tradeoffs.map((tradeoff, index) => (
                              <li key={`${country.code}-tradeoff-${index}`} data-testid={`tradeoff-country-risk-item-${country.code}-${index + 1}`}>{tradeoff}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {hasVerificationSection ? (
                        <div className={detailSectionClassName} data-testid={`tradeoff-country-verify-section-${country.code}`}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2" data-testid={`tradeoff-country-verify-heading-${country.code}`}>What deserves a closer look</p>
                          <ul className="list-disc list-inside space-y-2 text-sm leading-7 text-stone-600">
                            {verificationBullets.map((item, index) => (
                              <li key={`${country.code}-verify-${index}`} data-testid={`tradeoff-country-verify-item-${country.code}-${index + 1}`}>{softenVerificationGuidance(item, `${country.code}-${index}`)}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* What Could Still Change the Decision */}
        {decisionSensitivities.length ? (
        <section className="mb-12" data-testid="decision-sensitivities-section">
          <div className="rounded-[1.9rem] border border-stone-200/80 bg-stone-50/90 p-6 shadow-[0_18px_40px_rgba(28,25,23,0.05)]">
            <h2 className="font-manrope text-3xl text-stone-900 mb-3">What could still change the answer?</h2>
            <p className="text-sm leading-7 text-stone-600 max-w-3xl mb-4" data-testid="decision-sensitivities-helper-line">
              These are the final swing factors most likely to change the order after you compare the finalists more closely.
            </p>
            <ul className="space-y-3" data-testid="decision-sensitivities-list">
              {decisionSensitivities.map((item, index) => (
                <li
                  key={`decision-sensitivity-${index}`}
                  className="flex items-start gap-3 rounded-[1.4rem] border border-stone-200/80 bg-white/90 px-4 py-4 shadow-[0_12px_30px_rgba(28,25,23,0.04)]"
                  data-testid={`decision-sensitivity-${index + 1}`}
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" data-testid={`decision-sensitivity-icon-${index + 1}`} />
                  <p className="text-sm leading-7 text-stone-700">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
        ) : null}

        <section className="mb-12" data-testid="plan-compare-countries-section">
          <div className="rounded-[1.9rem] border border-orange-200/80 bg-[linear-gradient(180deg,rgba(255,247,237,0.95)_0%,rgba(255,252,248,0.98)_34%,rgba(255,247,237,0.94)_100%)] p-6 text-stone-900 shadow-[0_22px_48px_rgba(154,52,18,0.08)]">
            <div className="mb-4 h-1.5 w-14 rounded-full bg-amber-400/80" data-testid="plan-compare-accent" />
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="max-w-3xl">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-800" data-testid="plan-compare-kicker">Compare Your Finalists</p>
                <h2 className="mt-2 font-manrope text-3xl text-stone-900" data-testid="plan-compare-title">How do the finalists compare side by side?</h2>
                <p className="mt-3 text-sm leading-7 text-stone-700" data-testid="plan-compare-copy">
                  Compare your finalists side by side across monthly cost, healthcare access, residency realism, and day-to-day livability before moving forward.
                </p>
              </div>
              <div className="flex flex-col items-start gap-3">
                <Button asChild className="rc-button-accent" data-testid="plan-open-decision-matrix-button">
                  <Link to="/compare-countries">Compare Your Finalists</Link>
                </Button>
                <p className="text-sm leading-6 text-stone-600" data-testid="plan-compare-helper-text">
                  Your finalists are shown together so you can compare them side by side. If focusing on fewer countries helps clarify your decision, you can remove any finalist temporarily and restore the full comparison whenever you like.
                </p>
              </div>
            </div>
            <div className="mt-5 grid gap-3 md:grid-cols-3" data-testid="plan-compare-feature-grid">
              {[
                "See all of your finalists together",
                "Compare every finalist using the same priorities",
                "Focus on the tradeoffs most likely to change your decision"
              ].map((item, index) => (
                <div key={item} className="rounded-[1.25rem] border border-orange-200/70 bg-orange-50/45 px-4 py-4" data-testid={`plan-compare-feature-${index + 1}`}>
                  <p className="text-sm leading-7 text-stone-800">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Next Steps */}
        <section className="mb-12" data-testid="next-steps-section">
          <h2 className="font-manrope text-3xl text-stone-900 mb-3">What should you do next?</h2>
          <p className="text-sm leading-7 text-stone-600 max-w-3xl mb-4" data-testid="next-steps-helper-line">
            {`Use these steps to compare ${nextStepsComparisonLabel}, clear up the biggest tradeoff, and decide which country is ready for the next stage of planning.`}
          </p>
          <p className="text-sm leading-7 text-stone-600 max-w-3xl mb-4" data-testid="next-steps-framing-line">
            Finish these checks in order so each step reduces uncertainty before you commit to one country.
          </p>
          {activeComparisonCountries.length ? (
            <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="next-steps-country-links">
              <p className="text-sm leading-7 text-stone-600">Start with the country detail pages most relevant to the final comparison:</p>
              {activeComparisonCountries.map((country) => {
                const countrySlug = getCountryPreviewSlugByCode(country.code) || country.name.toLowerCase().replace(/\s+/g, "-");
                return (
                  <Link
                    key={`next-steps-country-link-${country.code}`}
                    to={`/country/${countrySlug}`}
                    className="text-sm font-medium text-amber-700 hover:text-amber-800 hover:underline"
                    data-testid={`next-steps-country-link-${country.code}`}
                  >
                    {`See what it's like to live in ${country.name} →`}
                  </Link>
                );
              })}
            </div>
          ) : null}
          <ol className="space-y-3">
            {nextSteps.map((step, index) => (
              <li
                key={`next-step-${index}`}
                className="flex items-start gap-4 rounded-[1.5rem] border border-stone-200/80 bg-white/90 px-5 py-4 shadow-[0_12px_30px_rgba(28,25,23,0.04)]"
                data-testid={`next-step-${index + 1}`}
              >
                <span
                  className="relative flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-[#4cad2f] ring-1 ring-[#275f19]/70 shadow-[0_0_12px_rgba(88,201,54,0.22)] sm:h-7 sm:w-7"
                  data-testid={`next-step-badge-${index + 1}`}
                >
                  <span className="absolute inset-[2px] rounded-full border border-[#2b6a1b]/25" aria-hidden="true" />
                  <span className="relative text-[12px] font-semibold text-white sm:text-[13px] [text-shadow:0_0_4px_rgba(126,243,255,0.58),0_0_5px_rgba(255,214,102,0.45)]">
                    {index + 1}
                  </span>
                </span>
                <p className="text-sm leading-7 text-stone-700">{step}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Relocation Timeline */}
        {sortedTimeline.length ? (
        <section className="mb-12" data-testid="relocation-timeline-section">
          <h2 className="font-manrope text-3xl text-stone-900 mb-4">In what order should you work through this?</h2>
          <p className="text-sm leading-7 text-stone-600 max-w-3xl mb-4" data-testid="relocation-timeline-helper-line">
            Work these phases in order: narrow the choice, check the strongest option in real conditions, choose one country to move forward with, and only then expand country-specific preparation.
          </p>
          <div className="space-y-5">
            {sortedTimeline.map((phase, index) => {
              const startMonth = phase.month || index + 1;
              const nextMonth = sortedTimeline[index + 1]?.month;
              const endMonth = nextMonth ? Math.max(startMonth, nextMonth - 1) : Math.max(startMonth, totalTimelineMonths);
              const monthLabel = endMonth > startMonth ? `Month ${startMonth}–${endMonth}` : `Month ${startMonth}`;
              const { Icon, accentClassName, label } = getTimelinePhaseMeta(phase.phase);
              const phaseSummary = tightenPlanPrecisionCopy(phase.summary || phase.tasks?.[0] || "");
              return (
                <div key={`${phase.phase}-${index}`} className="rounded-[1.9rem] border border-stone-200/80 bg-white/92 p-5 shadow-[0_18px_40px_rgba(28,25,23,0.05)]" data-testid={`timeline-phase-${index + 1}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3 max-w-3xl">
                      <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${accentClassName}`} data-testid={`timeline-phase-icon-shell-${index + 1}`}>
                        <Icon className="h-4 w-4" data-testid={`timeline-phase-icon-${index + 1}`} />
                      </div>
                      <div>
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-stone-500" data-testid={`timeline-phase-kicker-${index + 1}`}>
                            Phase {index + 1} · {label}
                          </p>
                          <span className="inline-flex rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 text-[11px] font-medium text-stone-600" data-testid={`timeline-phase-months-${index + 1}`}>
                            {monthLabel}
                          </span>
                        </div>
                        <h3 className="font-semibold text-stone-900 mb-2" data-testid={`timeline-phase-title-${index + 1}`}>
                          {phase.phase}
                        </h3>
                        {phaseSummary ? (
                          <p className="text-sm leading-7 text-stone-700" data-testid={`timeline-phase-summary-${index + 1}`}>{phaseSummary}</p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {Array.isArray(phase.tasks) && phase.tasks.length ? (
                    <ul className="mt-4 space-y-3 border-t border-stone-100 pt-4 text-sm text-stone-600" data-testid={`timeline-phase-tasks-${index + 1}`}>
                      {phase.tasks.map((task, i) => (
                        <li key={i} className="flex items-start gap-3" data-testid={`timeline-phase-task-${index + 1}-${i + 1}`}>
                          <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-600" />
                          <span>{task}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
        ) : null}

        {/* Page Actions */}
        <section className="pt-4 border-t border-stone-200/80" data-testid="plan-actions">
          <div className="flex flex-col gap-2 text-sm">
            <button
              data-testid="download-plan-link"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="text-left font-medium text-amber-700 hover:text-amber-800 hover:underline"
            >
              {downloadingPdf ? "Preparing PDF..." : "Download Your Relocation Blueprint (PDF)"}
            </button>
            <Link to="/assessment-results" className="font-medium text-amber-700 hover:text-amber-800 hover:underline" data-testid="back-to-results-link">
              Back to Assessment Results
            </Link>
          </div>
          <p className="text-xs text-stone-500 mt-4">
            This Relocation Blueprint reflects the preferences you had in place when it was created. If those inputs change later, you can generate a new Blueprint to match them.
          </p>
        </section>
      </main>

      <Footer />
      <PlanAvailabilityDialog open={showPlanAvailabilityDialog} onOpenChange={setShowPlanAvailabilityDialog} />
    </div>
  );
};

export default RelocationPlan;
